import { useEffect, useMemo, useState, type FormEvent } from "react";
import { NavLink, useNavigate, useSearchParams } from "react-router-dom";
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  BarChart3,
  CalendarDays,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock3,
  Copy,
  ExternalLink,
  FileText,
  Lightbulb,
  LockKeyhole,
  MessageCircle,
  MoreHorizontal,
  Plus,
  RefreshCw,
  Search,
  Send,
  ShieldCheck,
  Sparkles,
  Target,
  Telescope,
  TrendingUp,
  WandSparkles,
  X,
} from "lucide-react";
import { api, post } from "../api";
import type {
  Bootstrap,
  CaseItem,
  ContentItem,
  IntelligenceItem,
} from "../types";
import {
  Card,
  Empty,
  Eyebrow,
  PageIntro,
  Score,
  Status,
} from "../components/UI";

type PageProps = { data: Bootstrap };
type ActionProps = PageProps & { refresh: () => Promise<void> };

export function Press({data,refresh}:ActionProps){
  const [busy,setBusy]=useState(false),[error,setError]=useState(''),[view,setView]=useState<'releases'|'distribution'|'contacts'>('releases');
  const [selectedDistribution,setSelectedDistribution]=useState<any>(null);
  const [publicationForm,setPublicationForm]=useState(false),[contactForm,setContactForm]=useState(false),[selectedContact,setSelectedContact]=useState<any>(null);
  const foundation=data.press.foundation;
  async function newRelease(){
    setBusy(true);setError('');
    try { await post('/press',{headline:'Private release draft — replace before review',dek:'A sourced Minty News release prepared for internal review.',dateline:'Dubai, UAE',body:['This private draft is a local workspace fixture. It is not a public announcement and cannot be published from this screen.'],keyFacts:[{claim:'This draft is private and requires verified evidence before review.',sourceLabel:'Minty editorial standard',sourceUrl:'https://mintycoin.org/evidence'}],quotes:[],boilerplate:'MINTY is a Solana-based project with an evidence-led public information standard.',mediaContact:{name:'Minty Media',email:'contact@mintycoin.org'},slug:`private-release-${Date.now()}`,metadata:{seoTitle:'Private Minty release draft',seoDescription:'Internal editorial preview only.'},artworkId:null,relatedCaseIds:[],relatedCampaignIds:[]});await refresh();
    } catch(e){setError((e as Error).message)} finally {setBusy(false)}
  }
  async function submitPublication(event:FormEvent<HTMLFormElement>){event.preventDefault();setBusy(true);setError('');const form=new FormData(event.currentTarget);try{await post('/press/publications',{name:String(form.get('name')),canonicalDomain:String(form.get('domain')),publicationType:String(form.get('type')),description:String(form.get('description')||'')||null,primaryGeography:String(form.get('geography')),geographyCodes:[String(form.get('geography'))],languages:[String(form.get('language'))],audienceLevels:['SPECIALIST'],submissionUrl:null,beats:[{beatId:String(form.get('beat')),strength:'PRIMARY'}],provenance:{sourceType:'OFFICIAL_MASTHEAD',sourceUrl:String(form.get('sourceUrl')),sourceReference:null,collectionBasis:'MANUAL_RESEARCH',fieldCoverage:['PUBLICATION','BEATS','GEOGRAPHY'],evidenceNote:'Manually reviewed official publication source.',evidenceChecksum:null}});setPublicationForm(false);await refresh()}catch(e){setError((e as Error).message)}finally{setBusy(false)}}
  async function submitContact(event:FormEvent<HTMLFormElement>){event.preventDefault();setBusy(true);setError('');const form=new FormData(event.currentTarget);try{await post('/press/contacts',{displayName:String(form.get('name')),professionalTitle:String(form.get('title')||'')||null,contactType:String(form.get('type')),publicationId:String(form.get('publication')),roleTitle:String(form.get('title')||'')||null,email:String(form.get('email')),languages:[String(form.get('language'))],timezone:null,preferredMethod:'EMAIL',profileUrl:null,geographyCodes:[String(form.get('geography'))],beats:[{beatId:String(form.get('beat')),strength:'PRIMARY'}],provenance:{sourceType:'OFFICIAL_PROFILE',sourceUrl:String(form.get('sourceUrl')),sourceReference:null,collectionBasis:'MANUAL_RESEARCH',fieldCoverage:['NAME','TITLE','PUBLICATION','EMAIL','BEATS','GEOGRAPHY'],evidenceNote:'Manually reviewed official professional source.',evidenceChecksum:null}});setContactForm(false);await refresh()}catch(e){setError((e as Error).message)}finally{setBusy(false)}}
  async function openContact(id:string){setError('');try{setSelectedContact(await api(`/press/contacts/${id}`))}catch(e){setError((e as Error).message)}}
  async function verifySelected(){if(!selectedContact)return;setBusy(true);setError('');try{const contactProvenance=selectedContact.provenance.find((p:any)=>p.entity_type==='CONTACT'),channelProvenance=selectedContact.provenance.find((p:any)=>p.entity_type==='CHANNEL');if(!contactProvenance||!channelProvenance)throw new Error('This contact is missing exact provenance.');await post('/press/contact-verifications',{targetType:'CONTACT',targetId:selectedContact.id,provenanceId:contactProvenance.id,method:'OFFICIAL_PAGE_REVIEW',outcome:'VERIFIED',note:'Human reviewed the cited source.'});await post('/press/contact-verifications',{targetType:'CHANNEL',targetId:selectedContact.channel_id,provenanceId:channelProvenance.id,method:'OFFICIAL_PAGE_REVIEW',outcome:'VERIFIED',note:'Human reviewed the cited work channel.'});setSelectedContact(await api(`/press/contacts/${selectedContact.id}`));await refresh()}catch(e){setError((e as Error).message)}finally{setBusy(false)}}
  async function suppressSelected(){if(!selectedContact||!confirm('Mark this contact DO NOT CONTACT? Suppression overrides every future selection.'))return;setBusy(true);setError('');try{await post(`/press/contacts/${selectedContact.id}/suppress`,{channelId:selectedContact.channel_id,reason:'DO_NOT_CONTACT',source:'OPERATOR',note:'Explicit operator suppression.',reversible:false});setSelectedContact(await api(`/press/contacts/${selectedContact.id}`));await refresh()}catch(e){setError((e as Error).message)}finally{setBusy(false)}}
  async function startDistribution(releaseId:string){setBusy(true);setError('');try{const result=await post(`/press/releases/${releaseId}/distributions`,{});setSelectedDistribution(result);setView('distribution');await refresh()}catch(e){setError((e as Error).message)}finally{setBusy(false)}}
  async function openDistribution(id:string){setError('');try{setSelectedDistribution(await api(`/press/distributions/${id}`));setView('distribution')}catch(e){setError((e as Error).message)}}
  async function chooseRecipient(recipient:any,selection:'SELECTED'|'EXCLUDED'){if(!selectedDistribution)return;setBusy(true);setError('');try{setSelectedDistribution(await api(`/press/distributions/${selectedDistribution.id}/recipients`,{method:'PATCH',body:JSON.stringify({recipients:[{recipientId:recipient.id,selection,reason:null}]})}));}catch(e){setError((e as Error).message)}finally{setBusy(false)}}
  async function prepare(){if(!selectedDistribution)return;setBusy(true);setError('');try{setSelectedDistribution(await post(`/press/distributions/${selectedDistribution.id}/prepare`,{}));await refresh()}catch(e){setError((e as Error).message)}finally{setBusy(false)}}
  return <div className="page"><PageIntro eyebrow="MINTY PRESS" title="The story. On the record." body="Releases and media relationships stay sourced, private and under human control." actions={view==='releases'?<button className="button button-primary" disabled={busy} onClick={newRelease}><Plus/> {busy?'Starting…':'New release'}</button>:view==='contacts'?<div className="form-actions"><button className="button" onClick={()=>setPublicationForm(!publicationForm)}><Plus/>Publication</button><button className="button button-primary" disabled={!foundation.publications.length} onClick={()=>setContactForm(!contactForm)}><Plus/>Contact</button></div>:null}/><div className="source-switch"><button className={view==='releases'?'active':''} onClick={()=>setView('releases')}>Releases</button><button className={view==='distribution'?'active':''} onClick={()=>setView('distribution')}>Distribution</button><button className={view==='contacts'?'active':''} onClick={()=>setView('contacts')}>Contacts</button></div>{error&&<div className="alert alert-error">{error}</div>}{view==='releases'?<Card><Eyebrow>RELEASES</Eyebrow>{data.press.releases.length?<div className="stack-list">{data.press.releases.map(r=><article key={r.id}><Status>{r.status}</Status><h3>{r.headline}</h3><p>{r.dek??'No dek yet.'}</p><small>/{r.slug} · exact review fingerprint {r.fingerprint.slice(0,12)}…</small>{r.status==='APPROVED'&&<div className="form-actions"><button className="button" disabled={busy} onClick={()=>startDistribution(r.id)}><Send/>Send to press</button></div>}</article>)}</div>:<Empty title="No releases yet" body="Start with what happened. MINTY will keep the publication steps separate and reviewable."/>}</Card>:view==='distribution'?<><Card><div className="section-line"><div><Eyebrow>PRESS DISTRIBUTION</Eyebrow><h2>Recipient review, always human-controlled.</h2></div><Status>SENDING NOT CONFIGURED</Status></div><p>No delivery provider, sender worker or external email path exists.</p>{data.press.distributions.length?<div className="stack-list">{data.press.distributions.map(d=><button className="recent-drafts" key={d.id} onClick={()=>openDistribution(d.id)}><div><strong>{d.name}</strong><small>{d.headline} · {d.selected_count} selected</small></div><Status>{d.state}</Status></button>)}</div>:<Empty title="No preparation yet" body="Open an approved release and choose Send to press to review eligible recipients."/>}</Card>{selectedDistribution&&<Card><div className="section-line"><div><Eyebrow>RECIPIENT REVIEW</Eyebrow><h2>{selectedDistribution.name}</h2><p>Release V{selectedDistribution.release?.version??'—'} · fingerprint {(selectedDistribution.release?.fingerprint??selectedDistribution.release_fingerprint).slice(0,12)}…</p></div><Status>{selectedDistribution.state}</Status></div>{selectedDistribution.state==='STALE'?<p>This campaign is stale. The approved release changed; regenerate a new recipient review.</p>:<><div className="stack-list">{selectedDistribution.recipients.map((r:any)=><article key={r.id}><div className="section-line"><div><strong>{r.contact_snapshot?.displayName}</strong><small>{r.publication_snapshot?.name??'No publication'} · {r.score}/100 · {r.recommendation_band?.replace('_',' ')}</small></div><Status>{r.selection_state}</Status></div><p>{r.explanation?.map((e:any)=>e.message).join(' ')}</p>{r.is_eligible?<div className="form-actions"><button className="button" disabled={busy||selectedDistribution.state==='PREPARED'} onClick={()=>chooseRecipient(r,'SELECTED')}>Include</button><button className="button" disabled={busy||selectedDistribution.state==='PREPARED'} onClick={()=>chooseRecipient(r,'EXCLUDED')}>Exclude</button></div>:<p className="manual-only"><LockKeyhole/> Ineligible recipients cannot be selected.</p>}</article>)}</div><div className="form-actions"><button className="button button-primary" disabled={busy||selectedDistribution.state==='PREPARED'} onClick={prepare}>Prepare campaign</button><span className="manual-only"><LockKeyhole/> SENDING NOT CONFIGURED</span></div></>}</Card>}</>:<><Card><div className="section-line"><div><Eyebrow>MINTY CONTACTS ONLY</Eyebrow><h2>{foundation.contacts.length} contacts · {foundation.publications.length} publications</h2></div><Status>{foundation.delivery.reason}</Status></div><p>No sender, delivery worker or other TCOA contact source is connected. Every contact requires provenance and verification.</p></Card>{publicationForm&&<Card><Eyebrow>NEW PUBLICATION</Eyebrow><form onSubmit={submitPublication}><div className="form-grid"><label className="large-field">Publication name<input name="name" required/></label><label className="large-field">Canonical domain<input name="domain" placeholder="example.com" required/></label><label className="large-field">Type<select name="type" defaultValue="CRYPTO_NATIVE"><option>CRYPTO_NATIVE</option><option>FINANCIAL</option><option>TECHNOLOGY</option><option>REGIONAL</option><option>NEWSLETTER</option><option>PODCAST</option><option>OTHER</option></select></label><label className="large-field">Geography<select name="geography">{foundation.geographies.map(g=><option key={g.id} value={g.code}>{g.label}</option>)}</select></label><label className="large-field">Primary beat<select name="beat">{foundation.beats.map(b=><option key={b.id} value={b.id}>{b.label}</option>)}</select></label><label className="large-field">Language<select name="language"><option value="en">English</option></select></label><label className="large-field">Official source URL<input name="sourceUrl" type="url" required/></label><label className="large-field">Description<input name="description"/></label></div><div className="form-actions"><button type="button" className="button" onClick={()=>setPublicationForm(false)}>Cancel</button><button className="button button-primary" disabled={busy}>Save sourced publication</button></div></form></Card>}{contactForm&&<Card><Eyebrow>NEW CONTACT</Eyebrow><form onSubmit={submitContact}><div className="form-grid"><label className="large-field">Name<input name="name" required/></label><label className="large-field">Professional title<input name="title"/></label><label className="large-field">Publication<select name="publication">{foundation.publications.filter(p=>p.status==='ACTIVE').map(p=><option key={p.id} value={p.id}>{p.name}</option>)}</select></label><label className="large-field">Contact type<select name="type"><option>JOURNALIST</option><option>EDITOR</option><option>PRODUCER</option><option>NEWSLETTER_WRITER</option><option>PODCAST_HOST</option><option>PRESS_INBOX</option><option>SUBMISSION_DESK</option></select></label><label className="large-field">Work email<input name="email" type="email" required/></label><label className="large-field">Official source URL<input name="sourceUrl" type="url" required/></label><label className="large-field">Primary beat<select name="beat">{foundation.beats.map(b=><option key={b.id} value={b.id}>{b.label}</option>)}</select></label><label className="large-field">Geography<select name="geography">{foundation.geographies.map(g=><option key={g.id} value={g.code}>{g.label}</option>)}</select></label><input name="language" type="hidden" value="en"/></div><div className="form-actions"><button type="button" className="button" onClick={()=>setContactForm(false)}>Cancel</button><button className="button button-primary" disabled={busy}>Save unverified contact</button></div></form></Card>}<Card><Eyebrow>CONTACT DATABASE</Eyebrow>{foundation.contacts.length?<div className="stack-list">{foundation.contacts.map(c=><button className="recent-drafts" key={c.id} onClick={()=>openContact(c.id)}><div><strong>{c.display_name}</strong><small>{c.publication_name??'No current publication'} · {c.masked_value??'No channel'}</small></div><Status>{c.operational_status}</Status></button>)}</div>:<Empty title="No MINTY contacts yet" body="Add only legitimate crypto-media contacts with a recorded public or direct-business origin."/>}</Card>{selectedContact&&<Card><div className="section-line"><div><Eyebrow>CONTACT DETAIL</Eyebrow><h2>{selectedContact.display_name}</h2><p>{selectedContact.publication_name} · {selectedContact.masked_value}</p></div><Status>{selectedContact.operational_status}</Status></div><p>{selectedContact.provenance.length} provenance record(s) · {selectedContact.verifications.length} verification event(s) · {selectedContact.suppressions.filter((s:any)=>!s.reversed_at).length} active suppression(s)</p><div className="form-actions"><button className="button" disabled={busy||['DO_NOT_CONTACT','UNSUBSCRIBED','BOUNCED'].includes(selectedContact.operational_status)} onClick={verifySelected}><ShieldCheck/>Verify exact source</button><button className="button" disabled={busy||['DO_NOT_CONTACT','UNSUBSCRIBED','BOUNCED'].includes(selectedContact.operational_status)} onClick={suppressSelected}><LockKeyhole/>Do not contact</button><button className="text-button" onClick={()=>setSelectedContact(null)}>Close</button></div></Card>}</>}<p className="manual-only"><LockKeyhole/> Website publication, social publishing and press distribution remain separate. {foundation.delivery.reason}. Nothing here can send email.</p></div>;
}

/** Coverage is deliberately a private Press sub-workspace: every external claim stays reviewable. */
export function Coverage(){
  const [dashboard,setDashboard]=useState<any>(null),[selected,setSelected]=useState<any>(null),[busy,setBusy]=useState(false),[error,setError]=useState('');
  const load=async()=>{try{setDashboard(await api('/press/coverage'))}catch(e){setError((e as Error).message)}};
  useEffect(()=>{void load()},[]);
  const open=async(id:string)=>{try{setSelected(await api(`/press/coverage/${id}`))}catch(e){setError((e as Error).message)}};
  const review=async(decision:string)=>{if(!selected)return;setBusy(true);try{await post(`/press/coverage/${selected.item.id}/review`,{decision,note:'Operator review recorded in Minty HQ.'});await open(selected.item.id);await load()}catch(e){setError((e as Error).message)}finally{setBusy(false)}};
  const items=dashboard?.items??[];const normal=items.filter((item:any)=>['NEEDS_REVIEW','CONFIRMED'].includes(item.review_state));const reviewItems=items.filter((item:any)=>item.identity_confidence==='POSSIBLE'||item.review_state==='FETCH_PENDING'||item.review_state==='FETCH_FAILED');
  return <div className="page"><PageIntro eyebrow="MINTY PRESS · COVERAGE" title="Know who is really talking about MINTY." body="Every mention starts untrusted. Identity, backlinks and downstream actions stay under human review." actions={<button className="button" onClick={()=>void load()}><RefreshCw/>Refresh</button>}/>{error&&<div className="alert alert-error">{error}</div>}<div className="metric-grid"><Card><Eyebrow>NEW MENTIONS</Eyebrow><h2>{normal.filter((item:any)=>item.review_state==='NEEDS_REVIEW').length}</h2><p>Identity-supported items awaiting a decision.</p></Card><Card><Eyebrow>CONFIRMED</Eyebrow><h2>{normal.filter((item:any)=>item.review_state==='CONFIRMED').length}</h2><p>Human-confirmed coverage only.</p></Card><Card><Eyebrow>REVIEW</Eyebrow><h2>{reviewItems.length}</h2><p>Ambiguous or unavailable items never count as coverage.</p></Card></div><Card><div className="section-line"><div><Eyebrow>COVERAGE QUEUE</Eyebrow><h2>New mentions, confirmed and review</h2></div><Status>{dashboard?.adapters?.rss?'RSS ON':'RSS READY'}</Status></div>{items.length?<div className="stack-list">{items.map((item:any)=><button className="recent-drafts" key={item.id} onClick={()=>void open(item.id)}><div><strong>{item.title??item.canonical_domain??'Awaiting safe fetch'}</strong><small>{item.canonical_domain??'Unknown publication'} · {item.source_published_at?new Date(item.source_published_at).toLocaleDateString():'date unavailable'} · backlink {item.backlink_status??'UNKNOWN'}</small></div><Status>{item.identity_confidence} · {item.review_state}</Status></button>)}</div>:<Empty title="No coverage yet" body="That is an honest state. RSS and manual candidates pass through quarantine before appearing here."/>}</Card>{selected&&<Card><div className="section-line"><div><Eyebrow>COVERAGE REVIEW</Eyebrow><h2>{selected.item.title??'Untitled candidate'}</h2><p>{selected.item.final_url??selected.item.submitted_url}</p></div><Status>{selected.item.identity_confidence}</Status></div><p>{selected.item.metadata?.snippet??'No safe excerpt available.'}</p><div className="definition-list">{selected.evidence.map((e:any,i:number)=><Definition key={i} label={e.evidence_kind} value={e.explanation}/>)}</div><p className="manual-only">Backlink: {selected.backlinks?.[0]?.status??'UNKNOWN'} · {selected.backlinks?.[0]?.link_relation??'UNKNOWN'}. POSSIBLE and excluded items cannot become public proof, social content or website content.</p><div className="form-actions">{selected.item.review_state==='NEEDS_REVIEW'&&<><button className="button button-primary" disabled={busy} onClick={()=>void review('CONFIRM')}><Check/>Confirm</button><button className="button" disabled={busy} onClick={()=>void review('NOT_MINTY')}>Not MINTY</button><button className="button" disabled={busy} onClick={()=>void review('DISMISS')}>Dismiss</button></>}{selected.item.review_state==='CONFIRMED'&&<><button className="button" disabled={busy} onClick={async()=>{setBusy(true);try{await post(`/press/coverage/${selected.item.id}/social-opportunity`);await open(selected.item.id)}catch(e){setError((e as Error).message)}finally{setBusy(false)}}}>Create social opportunity</button><button className="button" disabled={busy} onClick={async()=>{setBusy(true);try{await post(`/press/coverage/${selected.item.id}/publication-candidate`);await open(selected.item.id)}catch(e){setError((e as Error).message)}finally{setBusy(false)}}}>Prepare website candidate</button></>}</div><p className="manual-only"><LockKeyhole/> No action here can publish, sign, email or send social content.</p></Card>}</div>;
}

function GrowthTabs() {
  return <nav className="growth-tabs" aria-label="Growth sections">
    <NavLink to="/growth" end>Overview</NavLink>
    <NavLink to="/growth/visibility">Visibility</NavLink>
    <a href="/growth#wallets">Holders</a>
  </nav>;
}

export function Today({ data }: PageProps) {
  const navigate = useNavigate();
  const thresholds = creativeThresholds(data);
  const ranked = everydayIntelligence(data)
    .filter((x) => x.estimated_freshness !== "STALE")
    .sort((a, b) => Number(b.alpha_candidate)-Number(a.alpha_candidate) || b.viral_score - a.viral_score);
  const lead = ranked.find((x) => x.viral_score >= thresholds.creativeReview && x.minty_fit_score >= thresholds.mintyFit && x.risk_rating !== "HIGH" && x.source_count > 0 && x.recommendation === "CREATE NOW");
  const leads = ranked.filter((x) => x.id !== lead?.id).slice(0, lead ? 2 : 3);
  const review = data.content.filter((x) =>
    ["HUMAN REVIEW", "AI CHECKED"].includes(x.status),
  );
  const scheduledToday = data.content.filter(
    (x) => x.scheduled_at && sameDay(new Date(x.scheduled_at), new Date()),
  );
  const failed = data.publishJobs.filter((x) =>
    ["FAILED", "ERROR"].includes(x.status),
  );
  const latest = data.publishJobs[0];
  return (
    <div className="today-page">
      <PageIntro
        eyebrow="MINTY TODAY"
        title={`${greeting()}, Detective.`}
        body="Here’s what matters, what needs you, and the clearest next move."
        actions={
          <div className="mission-mini">
            <span>First 100</span>
            <strong>
              {data.operation.progress}
              <em>/100</em>
            </strong>
          </div>
        }
      />
      {lead ? (
        <Card className="opportunity-hero">
          <div className="opportunity-copy">
            <Eyebrow>TODAY’S STRONGEST CASE</Eyebrow>
            <div className="signal-label">
              <i /> {lead.alpha_candidate?'ALPHA CANDIDATE':storyStatus(lead.viral_score, thresholds)} · {lead.information_stage}
            </div>
            <h2>{lead.title}</h2>
            <p className="lead-summary">{lead.summary}</p>
            <div className="recommendation">
              <Sparkles />
              <div>
                <span>
                  {momentumLabel(lead)} ·{" "}
                  {lead.recommended_media_type ?? "VISUAL CONCEPT"}
                </span>
                <strong>
                  {lead.angle?.detectiveAngle ??
                    lead.visual_concept ??
                    `Minty has a timely, credible angle while this story is moving.`}
                </strong>
              </div>
            </div>
            <div className="hero-actions">
              <button
                className="button button-primary button-large"
                onClick={() =>
                  navigate(`/create/media?type=FULL_CAMPAIGN&story=${lead.id}`)
                }
              >
                Create this <ArrowRight />
              </button>
              <button
                className="button button-subtle"
                onClick={() => navigate(`/discover?story=${lead.id}`)}
              >
                View case
              </button>
            </div>
          </div>
          <button
            className="score-focus"
            onClick={() => navigate(`/discover?story=${lead.id}`)}
            aria-label={`Opportunity score ${lead.viral_score}`}
          >
            <span>Opportunity</span>
            <strong>{lead.viral_score}</strong>
            <small>Minty fit {lead.minty_fit_score}</small>
          </button>
        </Card>
      ) : (
        <Card className="no-hot-hero">
          <Telescope />
          <Eyebrow>NO STRONG CASE RIGHT NOW</Eyebrow>
          <h2>Minty Intelligence is watching.</h2>
          <p>
            No current lead clears the 68-point Creative Director threshold,
            Minty-fit gate and basic safety review. No action recommended yet.
          </p>
          <button
            className="button button-subtle"
            onClick={() => navigate("/discover")}
          >
            Review current leads
          </button>
        </Card>
      )}
      <section className="briefing-section">
        <SectionHeading
          title={lead ? "Other strong leads" : "Best current leads"}
          action="See all"
          onClick={() => navigate("/discover")}
        />
        <div className="hot-grid">
          {leads.map((item, index) => (
            <button
              className="story-mini"
              key={item.id}
              onClick={() => navigate(`/discover?story=${item.id}`)}
            >
              <div>
                <span>{index + 1}</span>
                <Status tone={toneForScore(item.viral_score)}>
                  {storyStatus(item.viral_score, thresholds)}
                </Status>
              </div>
              <h3>{item.title}</h3>
              <footer>
                <span>
                  {momentumLabel(item)} · {item.recommendation}
                </span>
                <strong>{item.viral_score}</strong>
              </footer>
            </button>
          ))}
        </div>
        {!lead && leads.length > 0 && (
          <p className="no-action-note">
            BEST CURRENT LEAD · NO ACTION RECOMMENDED YET
          </p>
        )}
      </section>
      <div className="briefing-grid">
        <Card>
          <SectionHeading title="What needs me?" />
          <div className="attention-list">
            <AttentionRow
              count={review.length}
              label="posts awaiting approval"
              tone={review.length ? "amber" : "green"}
              onClick={() => navigate("/create/text?filter=review")}
            />
            <AttentionRow
              count={scheduledToday.length}
              label="scheduled today"
              tone="green"
              onClick={() => navigate("/calendar")}
            />
            <AttentionRow
              count={failed.length}
              label="publishing problems"
              tone={failed.length ? "red" : "green"}
              onClick={() => navigate("/distro-nine")}
            />
          </div>
        </Card>
        <Card>
          <SectionHeading title="What’s happening with Minty?" />
          <div className="snapshot-grid">
            <Snapshot
              value={`${data.growth.operation.verifiedExternalHolders}/100`}
              label="Verified First 100"
            />
            <Snapshot
              value={String(data.growth.today.buy_intent ?? 0)}
              label="Buy intent today"
            />
            <Snapshot
              value={String(data.growth.today.holder_signals ?? 0)}
              label="Holder signals today"
            />
            <Snapshot
              value={healthyAccountSummary(data)}
              label="Social activity"
            />
          </div>
        </Card>
      </div>
      <Card className="recent-card">
        <SectionHeading title="What happened recently?" />
        {latest ? (
          <div className="recent-event">
            <span
              className={`event-icon ${["FAILED", "ERROR"].includes(latest.status) ? "bad" : ""}`}
            >
              {["FAILED", "ERROR"].includes(latest.status) ? (
                <AlertCircle />
              ) : (
                <Check />
              )}
            </span>
            <div>
              <strong>{recentSentence(latest.status, latest.platform)}</strong>
              <p>
                {latest.content.title} · {relative(latest.created_at)}
              </p>
            </div>
            {latest.platform_post_url && (
              <a
                href={latest.platform_post_url}
                target="_blank"
                rel="noreferrer"
              >
                View post <ExternalLink />
              </a>
            )}
          </div>
        ) : (
          <div className="recent-event">
            <span className="event-icon">
              <Telescope />
            </span>
            <div>
              <strong>Minty is ready for the first move.</strong>
              <p>Choose a story and create the angle.</p>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}

export function Discover({ data, refresh }: ActionProps) {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("ALL");
  const [band, setBand] = useState("ALL");
  const [busy, setBusy] = useState("");
  const [notice, setNotice] = useState("");
  const thresholds = creativeThresholds(data);
  const canCreateDirectly = (item: IntelligenceItem) =>
    item.recommendation === "CREATE NOW" &&
    item.viral_score >= thresholds.creativeReview &&
    item.minty_fit_score >= thresholds.mintyFit &&
    item.estimated_freshness !== "STALE" &&
    item.risk_rating !== "HIGH" &&
    item.source_count > 0;
  const createRoute = (item: IntelligenceItem) =>
    `/create/media?type=FULL_CAMPAIGN&story=${item.id}${canCreateDirectly(item) ? "" : "&override=1"}`;
  const selected =
    data.intelligence.find((x) => x.id === params.get("story")) ?? null;
  const items = everydayIntelligence(data)
    .filter((i) =>
      `${i.title} ${i.category} ${i.narrative}`
        .toLowerCase()
        .includes(query.toLowerCase()),
    )
    .filter((i) => category === "ALL" || i.category.toUpperCase() === category)
    .filter((i) =>
      band === "ALL"
        ? true
        : band === "HOT"
          ? i.viral_score >= thresholds.hot
          : band === "GOOD CASE"
            ? i.viral_score >= thresholds.creativeReview && i.viral_score < thresholds.hot
            : i.viral_score >= 60 && i.viral_score < thresholds.creativeReview,
    )
    .sort(
      (a, b) =>
        Number(b.alpha_candidate)-Number(a.alpha_candidate) || b.viral_score - a.viral_score ||
        new Date(b.discovered_at).getTime() -
          new Date(a.discovered_at).getTime(),
    )
    .slice(0, 60);
  async function findAngle(item: IntelligenceItem) {
    setBusy(item.id);
    setNotice("");
    try {
      await post(`/intelligence/${item.id}/angle`);
      await refresh();
    } catch (e) {
      setNotice((e as Error).message);
    } finally {
      setBusy("");
    }
  }
  async function refreshDiscover() {
    setBusy("refresh-discover");
    setNotice("");
    try {
      const result = await post<{ sources: number; discovered: number; created: number; clustered: number; failed: number; paidCalls: number }>("/intelligence/refresh");
      await refresh();
      setNotice(
        result.created
          ? `Discover refreshed ${result.sources} high-signal sources and found ${result.created} new ${result.created === 1 ? "story" : "stories"}.`
          : `Discover checked ${result.sources} high-signal sources. You’re up to date.`,
      );
    } catch (error) {
      setNotice((error as Error).message);
    } finally {
      setBusy("");
    }
  }
  return (
    <>
      <PageIntro
        eyebrow="DISCOVER"
        title="What’s happening?"
        body="One narrative, one opportunity—ranked by momentum, Minty fit and visual potential."
        actions={<div className="discover-actions">
          <button className="button button-subtle" disabled={busy === "refresh-discover"} onClick={refreshDiscover}>
            <RefreshCw className={busy === "refresh-discover" ? "refreshing" : ""} />
            {busy === "refresh-discover" ? "Refreshing…" : "Refresh"}
          </button>
          <button className="button button-subtle" popoverTarget="new-story">
            <Plus />
            Add story
          </button>
        </div>}
      />
      {notice && <div className="toast">{notice}</div>}
      <div className="discover-toolbar">
        <div className="search-field">
          <Search />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search stories, themes or sources"
            aria-label="Search stories"
          />
        </div>
        <span>{items.length} opportunities</span>
      </div>
      <div className="intel-filters">
        <div>
          {[
            "ALL",
            "CRYPTO",
            "SOLANA",
            "VIRAL",
            "AI + TECH",
            "MEME CULTURE",
          ].map((value) => (
            <button
              key={value}
              className={category === value ? "active" : ""}
              onClick={() => setCategory(value)}
            >
              {value}
            </button>
          ))}
        </div>
        <div>
          {["ALL", "HOT", "GOOD CASE", "WATCH"].map((value) => (
            <button
              key={value}
              className={band === value ? "active" : ""}
              onClick={() => setBand(value)}
            >
              {value}
            </button>
          ))}
        </div>
      </div>
      <div className="story-grid">
        {items.map((item) => (
          <article className={`story-card ${item.distribution_state ? "story-card-distributed" : ""}`} data-distribution-state={item.distribution_state ?? undefined} key={item.id}>
            <button
              className="story-open"
              onClick={() => setParams({ story: item.id })}
            >
              <div className="story-top">
                <Status tone={item.distribution_state ? "bad" : toneForScore(item.viral_score, thresholds)}>
                  {item.distribution_state ?? (item.alpha_candidate?'ALPHA CANDIDATE':storyStatus(item.viral_score, thresholds))}
                </Status>
                <span>
                  {item.information_stage} · {relative(item.discovered_at)}
                </span>
              </div>
              <h2>{item.title}</h2>
              <p>{item.summary}</p>
              <div className="signal-strip">
                <span>{momentumLabel(item)}</span>
                <span>MINTY FIT {item.minty_fit_score}</span>
                <span>{item.taxonomy?.opportunityTypes?.[0] ?? item.intelligence_output_type}</span>
                {item.recommended_media_type && (
                  <span>{item.recommended_media_type}</span>
                )}
              </div>
              <div className="why-care">
                <Lightbulb />
                <span>
                  <b>{item.recommendation}</b>
                  {item.editorial_policy?.eligible === false ? item.editorial_policy.reason : item.angle?.whyItMatters ??
                    `${item.narrative} is being evaluated for a credible Minty entry.`}
                </span>
              </div>
              <footer>
                <span>
                  {item.source_count > 1
                    ? `${item.source_count} supporting signals`
                    : item.source_name}
                </span>
                <div>
                  <b>{item.viral_score}</b>
                  <small>V2 score</small>
                </div>
              </footer>
            </button>
            <button
              className="button button-primary button-wide"
              onClick={() => navigate(createRoute(item))}
              disabled={busy === item.id}
            >
              <Sparkles />
              {canCreateDirectly(item) ? "CREATE / SCHEDULE" : "CREATE ANYWAY"}
            </button>
          </article>
        ))}
        {!items.length && (
          <Empty
            title="No matching stories"
            body="Try a broader filter. Minty Intelligence is still watching."
          />
        )}
      </div>
      {selected && (
        <div
          className="focus-layer"
          role="presentation"
          onMouseDown={(e) => {
            if (e.currentTarget === e.target) setParams({});
          }}
        >
          <section
            className="angle-focus"
            role="dialog"
            aria-modal="true"
            aria-label="Minty angle"
          >
            <button
              className="close-button"
              onClick={() => setParams({})}
              aria-label="Close"
            >
              <X />
            </button>
            <div className="angle-focus-head">
              <div>
                <Eyebrow>
                  {selected.distribution_state ?? storyStatus(selected.viral_score, thresholds)} ·{" "}
                  {momentumLabel(selected)}
                </Eyebrow>
                <h2>{selected.title}</h2>
                <p>{selected.summary}</p>
                <a href={selected.source_url} target="_blank" rel="noreferrer">
                  {selected.source_name} <ExternalLink />
                </a>
                {selected.source_count > 1 && (
                  <small className="source-count">
                    Grouped from {selected.source_count} supporting signals
                  </small>
                )}
              </div>
              <Score value={selected.viral_score} size="lg" />
            </div>
            {selected.angle ? (
              <>
                <div className="angle-answer">
                  <Eyebrow>
                    {selected.recommendation} ·{" "}
                    {selected.recommended_media_type ?? "FORMAT PENDING"}
                  </Eyebrow>
                  <h3>{selected.angle.detectiveAngle}</h3>
                  <blockquote>{selected.angle.potentialHook}</blockquote>
                </div>
                <div className="why-it-works">
                  <Eyebrow>VISUAL CONCEPT</Eyebrow>
                  <p>
                    {selected.visual_concept ?? selected.angle.whyItMatters}
                  </p>
                </div>
                <div className="format-pills">
                  <Eyebrow>RECOMMENDED CONTENT</Eyebrow>
                  <div>
                    {selected.angle.contentFormats.slice(0, 4).map((format) => (
                      <span key={format}>{format}</span>
                    ))}
                  </div>
                </div>
                <div className="focus-actions">
                  {canCreateDirectly(selected) && (
                      <button
                        className="button button-primary button-large"
                        onClick={() =>
                          navigate(createRoute(selected))
                        }
                      >
                        CREATE <ArrowRight />
                      </button>
                    )}
                  {!canCreateDirectly(selected) && (
                    <button
                      className="button button-subtle button-large"
                      onClick={() => navigate(createRoute(selected))}
                    >
                      CREATE ANYWAY <ArrowRight />
                    </button>
                  )}
                  <button
                    className="button button-subtle"
                    onClick={() => {
                      setNotice("Set aside. Minty will keep watching.");
                      setParams({});
                    }}
                  >
                    Keep watching
                  </button>
                </div>
                <details className="progressive-details">
                  <summary>
                    Evidence and score <ChevronDown />
                  </summary>
                  <ScoreBars scores={selected.component_scores} />
                  <p>
                    Evidence: {selected.evidence_diversity} independent {selected.evidence_diversity === 1 ? "source" : "sources"}. {selected.freshness_reason}
                  </p>
                  {!!selected.taxonomy?.domains?.length && <p>Classification: {selected.taxonomy.domains.join(" · ")}</p>}
                  <p>{selected.angle.riskNotes}</p>
                  {selected.provenance?.map((source, index) => (
                    <a
                      key={`${source.sourceUrl}-${index}`}
                      href={source.sourceUrl}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {source.sourceRole ?? "SIGNAL"} · {source.sourceName}{" "}
                      <ExternalLink />
                    </a>
                  ))}
                </details>
              </>
            ) : (
              <div className="angle-pending">
                <Sparkles />
                <h3>Ready to find the Minty angle?</h3>
                <p>
                  Minty will return one clear editorial decision and visual
                  direction.
                </p>
                <button
                  className="button button-primary"
                  onClick={() => findAngle(selected)}
                  disabled={busy === selected.id}
                >
                  {busy ? "Thinking…" : "Find the Minty angle"}
                </button>
                <button className="button button-subtle" onClick={() => navigate(createRoute(selected))}>
                  {canCreateDirectly(selected) ? "CREATE / SCHEDULE" : "CREATE ANYWAY"} <ArrowRight />
                </button>
              </div>
            )}
          </section>
        </div>
      )}
      {notice && (
        <div className="toast" role="status">
          {notice}
        </div>
      )}
      <AddStory onSaved={refresh} />
    </>
  );
}

export function CreateStudio({ data, refresh }: ActionProps) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const stories = everydayIntelligence(data);
  const [createdId, setCreatedId] = useState<string | null>(null);
  const selectedId = params.get("content") ?? createdId;
  const selected =
    data.content.find((x) => x.id === selectedId) ??
    (params.get("filter") === "review"
      ? data.content.find((x) =>
          ["HUMAN REVIEW", "AI CHECKED"].includes(x.status),
        )
      : null);
  const [platform, setPlatform] = useState("Telegram");
  const [sourceMode, setSourceMode] = useState<"story" | "idea">("story");
  const [storyId, setStoryId] = useState(
    params.get("story") ?? stories[0]?.id ?? "",
  );
  const [idea, setIdea] = useState("");
  const [busy, setBusy] = useState("");
  const [notice, setNotice] = useState("");
  const [draft, setDraft] = useState(selected?.body ?? "");
  const [when, setWhen] = useState<"now" | "later">("now");
  const [scheduledAt, setScheduledAt] = useState(
    localDateTime(new Date(Date.now() + 3600000)),
  );
  const [account, setAccount] = useState("");
  const [success, setSuccess] = useState<{
    scheduled: boolean;
    platform: string;
  } | null>(null);
  useEffect(
    () => setDraft(selected?.body ?? ""),
    [selected?.id, selected?.body],
  );
  const matchingAccounts = data.bridge.accounts.filter(
    (a) =>
      a.active &&
      accountHealthy(a.connection_health.state) &&
      a.platform.toUpperCase() === selected?.platform.toUpperCase(),
  );
  useEffect(() => {
    if (
      matchingAccounts.length &&
      !matchingAccounts.some((a) => a.alias === account)
    )
      setAccount(matchingAccounts[0].alias);
  }, [selected?.id, data.bridge.accounts]);
  async function begin() {
    setBusy("begin");
    setNotice("");
    try {
      let intelId = storyId;
      if (sourceMode === "idea") {
        if (idea.trim().length < 5)
          throw new Error("Tell Minty what you want to say first.");
        const intel = await post<{ id: string }>("/intelligence", {
          title: idea.trim(),
          sourceName: "Minty idea",
          sourceUrl: "https://mintycoin.org/",
          category: "Minty",
          summary: idea.trim(),
          narrative: "MINTY ORIGINAL",
        });
        intelId = intel.id;
      }
      const item = data.intelligence.find((x) => x.id === intelId);
      if (!item?.angle) await post(`/intelligence/${intelId}/angle`);
      const opened = await post<{ id: string }>("/cases", {
        intelligenceItemId: intelId,
      });
      const format = formatFor(platform);
      const made = await post<{ id: string }>(
        `/cases/${opened.id}/content/generate`,
        { format, platform: platformFor(platform) },
      );
      await refresh();
      setCreatedId(made.id);
      navigate(`/create/text?content=${made.id}`, { replace: true });
      setNotice("Your draft is ready.");
    } catch (e) {
      setNotice((e as Error).message);
    } finally {
      setBusy("");
    }
  }
  async function act(
    label: string,
    fn: () => Promise<unknown>,
    message: string,
  ) {
    setBusy(label);
    setNotice("");
    try {
      await fn();
      await refresh();
      setNotice(message);
    } catch (e) {
      setNotice((e as Error).message);
    } finally {
      setBusy("");
    }
  }
  function improve(kind: string) {
    let next = draft;
    if (kind === "stronger")
      next = draft.replace(
        /^([^.!?]+)([.!?])?/,
        (_, line, punctuation) =>
          `${line.trim()}${punctuation || "."} Here’s why it matters:`,
      );
    if (kind === "funnier")
      next = `Detective Minty has entered the chat.\n\n${draft}`;
    if (kind === "shorter")
      next = draft.length > 280 ? `${draft.slice(0, 277).trim()}…` : draft;
    if (kind === "minty")
      next = `CASE OPENED 🔎\n\n${draft}\n\nFollow the evidence. Stay Minty.`;
    setDraft(next);
  }
  async function publish() {
    if (!selected) return;
    setBusy("publish");
    setNotice("");
    try {
      if (when === "later")
        await post(`/content/${selected.id}/transition`, {
          status: "SCHEDULED",
          scheduledAt: new Date(scheduledAt).toISOString(),
        });
      await post(`/content/${selected.id}/publish`, {
        account,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      await refresh();
      setSuccess({ scheduled: when === "later", platform: selected.platform });
    } catch (e) {
      setNotice((e as Error).message);
    } finally {
      setBusy("");
    }
  }
  if (success)
    return (
      <div className="success-screen">
        <div className="success-check">
          <Check />
        </div>
        <Eyebrow>{success.scheduled ? "SCHEDULED" : "ON ITS WAY"}</Eyebrow>
        <h1>
          {success.scheduled
            ? "Minty is on the calendar."
            : `Minty is heading to ${success.platform}.`}
        </h1>
        <p>
          {success.scheduled
            ? "The approved post will go out at the time you chose."
            : "The approved post has been handed off securely. You can leave this screen."}
        </p>
        <div>
          <button
            className="button button-primary"
            onClick={() => navigate("/calendar")}
          >
            View calendar
          </button>
          <button
            className="button button-subtle"
            onClick={() => {
              setSuccess(null);
              navigate("/");
            }}
          >
            Done
          </button>
        </div>
      </div>
    );
  return (
    <>
      <PageIntro
        eyebrow="CREATE"
        title="What are we making?"
        body="Choose the destination. Minty keeps the process simple and the safeguards underneath."
      />
      {!selected ? (
        <div className="create-start">
          <Card className="platform-card">
            <Eyebrow>1 · FORMAT</Eyebrow>
            <h2>Where will this live?</h2>
            <div className="platform-picker">
              {[
                "X",
                "Instagram",
                "Reel / TikTok",
                "Telegram",
                "Campaign",
                "Case File",
              ].map((p) => (
                <button
                  key={p}
                  className={platform === p ? "active" : ""}
                  onClick={() => setPlatform(p)}
                >
                  {platformIcon(p)}
                  <span>{p}</span>
                  {platform === p && <Check />}
                </button>
              ))}
            </div>
          </Card>
          <Card className="source-card">
            <Eyebrow>2 · SOURCE</Eyebrow>
            <h2>What starts the idea?</h2>
            <div className="source-switch">
              <button
                className={sourceMode === "story" ? "active" : ""}
                onClick={() => setSourceMode("story")}
              >
                Use a story
              </button>
              <button
                className={sourceMode === "idea" ? "active" : ""}
                onClick={() => setSourceMode("idea")}
              >
                Start from an idea
              </button>
            </div>
            {sourceMode === "story" ? (
              <label className="large-field">
                Story
                <select
                  value={storyId}
                  onChange={(e) => setStoryId(e.target.value)}
                >
                  {stories.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.viral_score} · {item.title}
                    </option>
                  ))}
                </select>
              </label>
            ) : (
              <label className="large-field">
                Your idea
                <textarea
                  value={idea}
                  onChange={(e) => setIdea(e.target.value)}
                  placeholder="What should Minty say?"
                />
              </label>
            )}
            <button
              className="button button-primary button-large button-wide"
              onClick={begin}
              disabled={
                busy === "begin" || (!storyId && sourceMode === "story")
              }
            >
              <WandSparkles />
              {busy === "begin"
                ? "Creating your draft…"
                : "Create the first draft"}
            </button>
          </Card>
          <Card className="recent-drafts">
            <SectionHeading title="Continue something" />
            {data.content.slice(0, 5).map((item) => (
              <button
                key={item.id}
                onClick={() => navigate(`/create/text?content=${item.id}`)}
              >
                <span
                  className={`content-dot dot-${simpleStatus(item.status).toLowerCase()}`}
                />
                <div>
                  <strong>{item.title}</strong>
                  <small>
                    {item.platform} · {simpleStatus(item.status)}
                  </small>
                </div>
                <ChevronRight />
              </button>
            ))}
            {!data.content.length && (
              <Empty
                title="Nothing in progress"
                body="Your first draft will appear here."
              />
            )}
          </Card>
        </div>
      ) : (
        <div className="studio-layout">
          <aside className="studio-rail">
            <button className="back-link" onClick={() => navigate("/create")}>
              <ArrowLeft /> New content
            </button>
            <div className="workflow-steps">
              <Step
                n="1"
                label="Create"
                active={true}
                done={selected.status !== "DRAFT"}
              />
              <Step
                n="2"
                label="Approve"
                active={["AI CHECKED", "HUMAN REVIEW"].includes(
                  selected.status,
                )}
                done={["APPROVED", "SCHEDULED", "PUBLISHED"].includes(
                  selected.status,
                )}
              />
              <Step
                n="3"
                label="Publish"
                active={["APPROVED", "SCHEDULED"].includes(selected.status)}
                done={selected.status === "PUBLISHED"}
              />
            </div>
            <div className="draft-list">
              <span>RECENT</span>
              {data.content.slice(0, 6).map((item) => (
                <button
                  key={item.id}
                  className={item.id === selected.id ? "active" : ""}
                  onClick={() => navigate(`/create/text?content=${item.id}`)}
                >
                  {item.title}
                  <small>{simpleStatus(item.status)}</small>
                </button>
              ))}
            </div>
          </aside>
          <div className="studio-main">
            <Card className="editor-card">
              <div className="editor-title">
                <div>
                  <Eyebrow>
                    {selected.platform} · {selected.format}
                  </Eyebrow>
                  <h2>{selected.title}</h2>
                </div>
                <Status tone={statusTone(selected.status)}>
                  {simpleStatus(selected.status)}
                </Status>
              </div>
              <textarea
                className="content-editor"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                aria-label="Content draft"
              />
              <div className="assistant-actions">
                <span>IMPROVE THIS</span>
                <button onClick={() => improve("stronger")}>
                  Make stronger
                </button>
                <button onClick={() => improve("funnier")}>Make funnier</button>
                <button onClick={() => improve("shorter")}>Make shorter</button>
                <button onClick={() => improve("minty")}>More Minty</button>
                <button
                  onClick={() =>
                    act(
                      "again",
                      async () => {
                        const made = await post<{ id: string }>(
                          `/cases/${selected.case_id}/content/generate`,
                          {
                            format: selected.format,
                            platform: selected.platform,
                          },
                        );
                        setCreatedId(made.id);
                        navigate(`/create/text?content=${made.id}`, {
                          replace: true,
                        });
                      },
                      "A fresh version is ready.",
                    )
                  }
                  disabled={!!busy}
                >
                  <RefreshCw />
                  Try again
                </button>
              </div>
              <div className="editor-actions">
                <span>{draft.length} characters</span>
                <button
                  className="button button-subtle"
                  onClick={() =>
                    act(
                      "save",
                      () =>
                        post(`/content/${selected.id}/update`, { body: draft }),
                      "Saved.",
                    )
                  }
                  disabled={busy === "save"}
                >
                  Save
                </button>
                <button
                  className="button button-primary"
                  onClick={() =>
                    act(
                      "check",
                      async () => {
                        if (draft !== selected.body)
                          await post(`/content/${selected.id}/update`, {
                            body: draft,
                          });
                        return post(`/content/${selected.id}/check`);
                      },
                      "Checks complete.",
                    )
                  }
                  disabled={busy === "check"}
                >
                  <ShieldCheck />
                  {busy === "check" ? "Checking…" : "Check facts"}
                </button>
              </div>
            </Card>
            {selected.claim_check && (
              <Card
                className={`ready-card ${selected.claim_check.status === "BLOCKED" ? "needs-attention" : ""}`}
              >
                <Eyebrow>
                  {selected.claim_check.status === "BLOCKED"
                    ? "NEEDS ATTENTION"
                    : "READY TO GO?"}
                </Eyebrow>
                <h2>
                  {selected.claim_check.status === "BLOCKED"
                    ? "One thing needs fixing."
                    : "This is ready for your decision."}
                </h2>
                <div className="trust-list">
                  <Trust
                    ok={selected.claim_check.status !== "BLOCKED"}
                    label="Facts checked"
                  />
                  <Trust ok={true} label="Minty voice" />
                  <Trust
                    ok={selected.claim_check.status !== "BLOCKED"}
                    label="Ready"
                  />
                </div>
                {selected.claim_check.status === "BLOCKED" ? (
                  <details>
                    <summary>What needs attention</summary>
                    {selected.claim_check.claims.map((claim, i) => (
                      <p key={i}>{claim.reason}</p>
                    ))}
                  </details>
                ) : (
                  !["APPROVED", "SCHEDULED", "PUBLISHED"].includes(
                    selected.status,
                  ) && (
                    <button
                      className="button button-primary button-large"
                      onClick={() =>
                        act(
                          "approve",
                          () =>
                            post(`/content/${selected.id}/transition`, {
                              status: "APPROVED",
                            }),
                          "Approved. Choose when and where it should go.",
                        )
                      }
                      disabled={busy === "approve"}
                    >
                      <Check />
                      Approve
                    </button>
                  )
                )}
              </Card>
            )}
            {["APPROVED", "SCHEDULED"].includes(selected.status) && (
              <Card className="publish-card">
                <Eyebrow>PUBLISH</Eyebrow>
                <h2>When should this go out?</h2>
                <div className="when-picker">
                  <button
                    className={when === "now" ? "active" : ""}
                    onClick={() => setWhen("now")}
                  >
                    <Send />
                    Now<small>Publish as soon as possible</small>
                  </button>
                  <button
                    className={when === "later" ? "active" : ""}
                    onClick={() => setWhen("later")}
                  >
                    <Clock3 />
                    Later<small>Choose a date and time</small>
                  </button>
                </div>
                {when === "later" && (
                  <label className="large-field">
                    Date and time
                    <input
                      type="datetime-local"
                      value={scheduledAt}
                      onChange={(e) => setScheduledAt(e.target.value)}
                    />
                  </label>
                )}
                <h3>Where?</h3>
                <div className="account-picker">
                  {matchingAccounts.map((item) => (
                    <button
                      key={item.id}
                      className={account === item.alias ? "active" : ""}
                      onClick={() => setAccount(item.alias)}
                    >
                      <span className="account-icon">
                        {item.platform.slice(0, 1)}
                      </span>
                      <span>
                        <strong>{item.display_name ?? item.handle}</strong>
                        <small>{item.platform} · Connected</small>
                      </span>
                      {account === item.alias && <Check />}
                    </button>
                  ))}
                </div>
                {!matchingAccounts.length && (
                  <div className="soft-warning">
                    <AlertCircle />
                    <span>
                      No healthy {selected.platform} account is available.
                      Choose another format or reconnect the account in More.
                    </span>
                  </div>
                )}
                <button
                  className="button button-primary button-large button-wide"
                  onClick={publish}
                  disabled={!account || busy === "publish"}
                >
                  {when === "later" ? <CalendarDays /> : <Send />}
                  {busy === "publish"
                    ? "Sending securely…"
                    : when === "later"
                      ? "Schedule"
                      : "Publish"}
                </button>
                <p className="safety-note">
                  <LockKeyhole /> Manual approval only. Nothing is published
                  automatically.
                </p>
              </Card>
            )}
            {notice && (
              <div className="inline-notice" role="status">
                {notice}
              </div>
            )}
          </div>
        </div>
      )}
      {notice && !selected && (
        <div className="toast" role="status">
          {notice}
        </div>
      )}
    </>
  );
}

export function Calendar({ data }: PageProps) {
  const navigate = useNavigate();
  const [month, setMonth] = useState(startMonth(new Date()));
  const days = calendarDays(month);
  const events = data.content.filter((x) => x.scheduled_at || x.published_at);
  return (
    <>
      <PageIntro
        eyebrow="CALENDAR"
        title="What’s going out?"
        body="A calm view of every draft, scheduled post and published story."
        actions={
          <button
            className="button button-primary"
            onClick={() => navigate("/create")}
          >
            <Plus />
            Create
          </button>
        }
      />
      <Card className="calendar-card">
        <div className="calendar-toolbar">
          <button
            onClick={() => setMonth(addMonths(month, -1))}
            aria-label="Previous month"
          >
            <ChevronRight />
          </button>
          <h2>
            {month.toLocaleDateString("en", { month: "long", year: "numeric" })}
          </h2>
          <button
            onClick={() => setMonth(addMonths(month, 1))}
            aria-label="Next month"
          >
            <ChevronRight />
          </button>
          <button
            className="text-button"
            onClick={() => setMonth(startMonth(new Date()))}
          >
            Today
          </button>
        </div>
        <div className="weekday-row">
          {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
            <span key={d}>{d}</span>
          ))}
        </div>
        <div className="calendar-grid">
          {days.map((day) => (
            <button
              key={day.toISOString()}
              className={`${day.getMonth() !== month.getMonth() ? "outside" : ""} ${sameDay(day, new Date()) ? "today" : ""}`}
              onClick={() => navigate(`/create?date=${day.toISOString()}`)}
            >
              <span>{day.getDate()}</span>
              {events
                .filter((e) =>
                  sameDay(new Date(e.scheduled_at ?? e.published_at!), day),
                )
                .slice(0, 3)
                .map((e) => (
                  <article
                    key={e.id}
                    onClick={(event) => {
                      event.stopPropagation();
                      navigate(`/create/text?content=${e.id}`);
                    }}
                    className={`event-${simpleStatus(e.status).toLowerCase()}`}
                  >
                    <strong>{e.title}</strong>
                    <small>{simpleStatus(e.status)}</small>
                  </article>
                ))}
            </button>
          ))}
        </div>
      </Card>
      <div className="calendar-legend">
        {["Draft", "Ready", "Scheduled", "Published", "Problem"].map((s) => (
          <span key={s}>
            <i className={`dot-${s.toLowerCase()}`} />
            {s}
          </span>
        ))}
      </div>
    </>
  );
}

export function Growth({ data, refresh }: ActionProps) {
  const g = data.growth;
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  async function classify(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setNotice("");
    try {
      const form = new FormData(event.currentTarget);
      const evidenceFact = String(form.get("evidenceFact") || "").trim();
      const evidenceTransaction = String(
        form.get("evidenceTransaction") || "",
      ).trim();
      await post("/growth/wallet-classifications", {
        wallet: form.get("wallet"),
        label: form.get("label"),
        type: form.get("type"),
        ownershipStatus: form.get("ownershipStatus"),
        purpose: form.get("purpose") || undefined,
        role: form.get("role") || undefined,
        evidenceState: form.get("evidenceState"),
        evidence: evidenceFact
          ? [
              {
                fact: evidenceFact,
                ...(evidenceTransaction
                  ? { transaction: evidenceTransaction }
                  : {}),
              },
            ]
          : [],
        reviewSource: form.get("reviewSource") || undefined,
        includeInHolderCount: form.get("includeInHolderCount") === "on",
        notes: form.get("notes") || undefined,
      });
      await refresh();
      event.currentTarget.reset();
      setNotice(
        "Wallet classification and evidence history saved. It will affect the next verified snapshot.",
      );
    } catch (error) {
      setNotice((error as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="growth-v1">
      <PageIntro
        eyebrow="MINTY GROWTH"
        title="Operation First 100"
        body="Attention is not growth. This is the evidence connecting campaigns, visits, Buy intent and defensible on-chain signals."
      />
      <GrowthTabs />
      <Card className="growth-hero">
        <div className="growth-number">
          <strong>{g.operation.verifiedExternalHolders}</strong>
          <span>—</span>
          <em>100</em>
          <small>verified external holders</small>
        </div>
        <div className="growth-track">
          <i style={{ width: `${g.operation.verifiedExternalHolders}%` }} />
          {[10, 25, 50, 100].map((n) => (
            <b key={n} style={{ left: `${n}%` }}>
              <span>{n}</span>
            </b>
          ))}
        </div>
        <div className="first-ten" aria-label="Operation First 10">
          {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
            <span
              className={
                g.operation.verifiedExternalHolders >= n ? "reached" : ""
              }
              key={n}
            >
              {n}
            </span>
          ))}
        </div>
        <p>{g.operation.methodology}</p>
      </Card>
      <div className="growth-metrics">
        <Metric
          value={String(g.today.website_visits ?? 0)}
          label="Website visits today"
        />
        <Metric
          value={String(g.today.buy_intent ?? 0)}
          label="Buy intent today"
        />
        <Metric
          value={String(g.today.holder_signals ?? 0)}
          label="New holder signals"
        />
        <Metric
          value={String(g.today.published_campaigns ?? 0)}
          label="Published campaigns"
        />
      </div>
      <Card>
        <Eyebrow>MINTY GROWTH — TODAY</Eyebrow>
        <h2>{g.dailyBrief.recommendation}</h2>
        <div className="definition-list">
          <Definition label="Best campaign" value={g.dailyBrief.bestCampaign} />
          <Definition
            label="Most effective platform"
            value={g.dailyBrief.bestPlatform}
          />
          <Definition
            label="Most effective format"
            value={g.dailyBrief.bestFormat}
          />
        </div>
      </Card>
      <Card className="learning-card">
        <div className="learning-icon">
          <TrendingUp />
        </div>
        <div>
          <Eyebrow>WHAT’S WORKING?</Eyebrow>
          <h2>{g.learning.message}</h2>
          <p>
            Recommendations require at least {g.learning.minimumCampaigns}{" "}
            measured campaigns with real traffic. Likes alone do not qualify.
          </p>
        </div>
      </Card>
      <div className="growth-two-col">
        <Card>
          <Eyebrow>LIVE BASELINE</Eyebrow>
          <h2>What exists on-chain?</h2>
          <div className="definition-list">
            <Definition
              label="Token accounts"
              value={String(g.operation.tokenAccounts ?? "UNKNOWN")}
            />
            <Definition
              label="Resolved wallets"
              value={String(g.operation.wallets ?? "UNKNOWN")}
            />
            <Definition
              label="Project / infrastructure"
              value={String(g.operation.knownProjectAccounts ?? "UNKNOWN")}
            />
            <Definition
              label="Unknown accounts"
              value={String(g.operation.unknownAccounts ?? "UNKNOWN")}
            />
            <Definition
              label="Verified external holders"
              value={String(g.operation.verifiedExternalHolders ?? "UNKNOWN")}
            />
            <Definition
              label="Observed"
              value={
                g.operation.observedAt
                  ? date(g.operation.observedAt)
                  : "Waiting for live snapshot"
              }
            />
          </div>
        </Card>
        <Card>
          <Eyebrow>MARKET · {g.market?.stale ? "STALE" : "LIVE"}</Eyebrow>
          <h2>
            {g.market
              ? `$${Number(g.market.liquidity_usd ?? 0).toFixed(2)} liquidity`
              : "Market snapshot pending"}
          </h2>
          <div className="definition-list">
            <Definition
              label="24h volume"
              value={
                g.market
                  ? `$${Number(g.market.volume_24h_usd ?? 0).toFixed(2)}`
                  : "UNKNOWN"
              }
            />
            <Definition
              label="Transactions"
              value={String(g.market?.transactions_24h ?? "UNKNOWN")}
            />
            <Definition
              label="Buys / sells"
              value={
                g.market
                  ? `${g.market.buys ?? "UNKNOWN"} / ${g.market.sells ?? "UNKNOWN"}`
                  : "UNKNOWN"
              }
            />
            <Definition
              label="Source"
              value={String(g.market?.source ?? "UNKNOWN")}
            />
          </div>
        </Card>
      </div>
      <Card>
        <Eyebrow>GROWTH TIMELINE</Eyebrow>
        <h2>What happened, in order.</h2>
        <div className="growth-timeline">
          {g.timeline.length ? (
            g.timeline.slice(0, 12).map((item, i) => (
              <div key={`${item.occurred_at}-${i}`}>
                <time>
                  {new Date(String(item.occurred_at)).toLocaleString()}
                </time>
                <strong>{humanize(String(item.type))}</strong>
                <span>
                  {String(item.source)}
                  {item.entity ? ` · ${String(item.entity)}` : ""}
                </span>
              </div>
            ))
          ) : (
            <Empty
              title="No real growth events yet"
              body="The baseline is live. Campaign visits, Buy intent and holder changes will appear here."
            />
          )}
        </div>
      </Card>
      <Card>
        <Eyebrow>CAMPAIGN PERFORMANCE</Eyebrow>
        <h2>Attention versus intent.</h2>
        {g.campaigns.length ? (
          <div className="growth-table">
            {g.campaigns.map((item, i) => (
              <div key={i}>
                <strong>{String(item.campaign_key)}</strong>
                <span>{String(item.source_key ?? "direct")}</span>
                <b>{String(item.visits)} visits</b>
                <b>{String(item.buy_intent)} Buy intent</b>
                <Status>
                  {Number(item.buy_intent) > 0 ? "DIRECT" : "NO ATTRIBUTION"}
                </Status>
              </div>
            ))}
          </div>
        ) : (
          <Empty
            title="We’re still learning"
            body="No real tracked campaign has produced enough first-party activity to compare yet."
          />
        )}
      </Card>
      <div className="growth-two-col">
        <Card>
          <Eyebrow>GROWTH ALERTS</Eyebrow>
          <h2>Only material signals.</h2>
          {g.alerts.length ? (
            <div className="growth-timeline">
              {g.alerts.slice(0, 6).map((alert, i) => (
                <div key={`${alert.id}-${i}`}>
                  <time>{date(String(alert.observed_at))}</time>
                  <strong>{String(alert.title)}</strong>
                  <span>{String(alert.detail)}</span>
                </div>
              ))}
            </div>
          ) : (
            <Empty
              title="No new alerts"
              body="Material liquidity changes, new token-account signals and verified milestones will appear here."
            />
          )}
        </Card>
        <Card>
          <Eyebrow>COINGECKO READINESS</Eyebrow>
          <h2>{g.coinGeckoReadiness.state}</h2>
          <div className="definition-list">
            {Object.entries(g.coinGeckoReadiness.checks).map(([key, ready]) => (
              <Definition
                key={key}
                label={humanize(key)}
                value={ready ? "READY" : "NOT READY"}
              />
            ))}
          </div>
          <p>{g.coinGeckoReadiness.note}</p>
        </Card>
      </div>
      <details className="growth-advanced" id="wallets">
        <summary>
          View wallets and methodology <ChevronDown />
        </summary>
        <div className="growth-two-col">
          <Card>
            <Eyebrow>CONFIDENCE</Eyebrow>
            <h2>Claim only what we can prove.</h2>
            <p>
              A finalized transaction proves an on-chain action. It does not
              prove who controls a wallet, that a wallet is independent, or that
              it represents one person. Only a wallet classified{" "}
              <strong>EXTERNAL + VERIFIED</strong> may count toward Operation
              First 100.
            </p>
            <p>
              Every classification change is retained with its evidence, source,
              reviewer and timestamp. UNKNOWN stays excluded.
            </p>
          </Card>
          <Card>
            <Eyebrow>CLASSIFY A WALLET</Eyebrow>
            <form className="wallet-form" onSubmit={classify}>
              <input
                name="wallet"
                required
                placeholder="Solana wallet address"
              />
              <input name="label" required placeholder="Internal label" />
              <input name="role" placeholder="Role (for example Treasury)" />
              <select name="type" defaultValue="UNKNOWN">
                <option>UNKNOWN</option>
                <option>PROJECT</option>
                <option>TREASURY</option>
                <option>LIQUIDITY</option>
                <option>DEX</option>
                <option>BURN</option>
                <option>EXTERNAL</option>
              </select>
              <select name="ownershipStatus" defaultValue="UNKNOWN">
                <option>UNKNOWN</option>
                <option>UNVERIFIED</option>
                <option>VERIFIED</option>
              </select>
              <select name="evidenceState" defaultValue="UNKNOWN">
                <option>UNKNOWN</option>
                <option>VERIFIED ON-CHAIN</option>
                <option>VERIFIED PROJECT RECORD</option>
                <option>VERIFIED ON-CHAIN + PROJECT RECORD</option>
                <option>LIKELY / INFERRED</option>
              </select>
              <input name="purpose" placeholder="Purpose (optional)" />
              <input name="reviewSource" placeholder="Review source" />
              <input
                name="evidenceTransaction"
                placeholder="Transaction signature (optional)"
              />
              <textarea
                name="evidenceFact"
                placeholder="What this evidence proves"
              />
              <textarea
                name="notes"
                placeholder="Classification reasoning and limits"
              />
              <label>
                <input type="checkbox" name="includeInHolderCount" /> Include in
                verified-holder count
              </label>
              <button className="button button-primary" disabled={busy}>
                {busy ? "Saving…" : "Save with history"}
              </button>
            </form>
          </Card>
        </div>
        <div className="growth-two-col">
          <Card>
            <Eyebrow>PLATFORM & FORMAT</Eyebrow>
            <h2>Measured routes.</h2>
            {g.breakdowns.platforms.length ? (
              <div className="definition-list">
                {g.breakdowns.platforms.slice(0, 4).map((item, i) => (
                  <Definition
                    key={`p-${i}`}
                    label={`Platform · ${String(item.name)}`}
                    value={`${item.visits} visits · ${item.buy_intent} intent`}
                  />
                ))}
                {g.breakdowns.formats.slice(0, 4).map((item, i) => (
                  <Definition
                    key={`f-${i}`}
                    label={`Format · ${String(item.name)}`}
                    value={`${item.visits} visits · ${item.buy_intent} intent`}
                  />
                ))}
              </div>
            ) : (
              <p>UNKNOWN — no attributable traffic yet.</p>
            )}
          </Card>
          <Card>
            <Eyebrow>NARRATIVE LEARNING</Eyebrow>
            <h2>Real actions, not likes.</h2>
            {g.breakdowns.narratives.length ? (
              <div className="definition-list">
                {g.breakdowns.narratives.slice(0, 6).map((item, i) => (
                  <Definition
                    key={i}
                    label={String(item.name)}
                    value={`${item.visits} visits · ${item.buy_intent} intent`}
                  />
                ))}
              </div>
            ) : (
              <p>UNKNOWN — insufficient measured campaigns.</p>
            )}
          </Card>
        </div>
        <div className="wallet-ledger">
          {g.wallets.map((wallet, i) => {
          const evidence = Array.isArray(wallet.evidence)
            ? (wallet.evidence as Array<{ fact?: unknown; transaction?: string }>)
              : [];
            const history = Array.isArray(wallet.history)
              ? (wallet.history as Array<Record<string, unknown>>)
              : [];
            const tokenAccounts = Array.isArray(wallet.token_account_list)
              ? (wallet.token_account_list as Array<Record<string, unknown>>)
              : [];
            return (
              <article className="wallet-card" key={String(wallet.wallet ?? i)}>
                <header>
                  <div>
                    <Eyebrow>
                      {String(wallet.type ?? "UNKNOWN")} ·{" "}
                      {String(wallet.evidence_state ?? "UNKNOWN")}
                    </Eyebrow>
                    <h3>{String(wallet.label ?? "Unclassified wallet")}</h3>
                    <a
                      href={`https://solscan.io/account/${String(wallet.wallet)}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <code>{String(wallet.wallet)}</code>
                      <ExternalLink />
                    </a>
                  </div>
                  <Status>
                    {wallet.include_in_holder_count ? "COUNTED" : "EXCLUDED"}
                  </Status>
                </header>
                <div className="wallet-stats">
                  <span>
                    <small>Balance</small>
                    <strong>{mintyAmount(wallet.balance)} MINTY</strong>
                  </span>
                  <span>
                    <small>Observed supply</small>
                    <strong>
                      {Number(wallet.percentage ?? 0).toFixed(4)}%
                    </strong>
                  </span>
                  <span>
                    <small>Role</small>
                    <strong>{String(wallet.role ?? "UNKNOWN")}</strong>
                  </span>
                  <span>
                    <small>First seen</small>
                    <strong>
                      {wallet.first_seen
                        ? date(String(wallet.first_seen))
                        : "UNKNOWN"}
                    </strong>
                  </span>
                </div>
                <details>
                  <summary>
                    Why this classification? <ChevronDown />
                  </summary>
                  <div>
                    <p>
                      {String(
                        wallet.notes ??
                          "No classification reasoning has been recorded.",
                      )}
                    </p>
                    <div className="wallet-meta">
                      <span>
                        Ownership ·{" "}
                        {String(wallet.ownership_status ?? "UNKNOWN")}
                      </span>
                      <span>
                        Source ·{" "}
                        {String(wallet.review_source ?? "Not recorded")}
                      </span>
                      <span>
                        Reviewed ·{" "}
                        {wallet.reviewed_at
                          ? date(String(wallet.reviewed_at))
                          : "Not reviewed"}
                      </span>
                      <span>
                        {String(wallet.token_accounts)} token account
                        {Number(wallet.token_accounts) === 1 ? "" : "s"}
                      </span>
                    </div>
                    {evidence.length > 0 && (
                      <div className="wallet-evidence">
                        <strong>Evidence</strong>
                        {evidence.map((item, j) => (
                          <div key={j}>
                            <span>
                              {String(item.fact ?? "Evidence record")}
                            </span>
                            {item.transaction && (
                              <a
                                href={`https://solscan.io/tx/${String(item.transaction)}`}
                                target="_blank"
                                rel="noreferrer"
                              >
                                View transaction <ExternalLink />
                              </a>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="wallet-evidence">
                      <strong>Token accounts</strong>
                      {tokenAccounts.map((item, j) => (
                        <div key={j}>
                          <a
                            href={`https://solscan.io/account/${String(item.tokenAccount)}`}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {shortAddress(String(item.tokenAccount))}{" "}
                            <ExternalLink />
                          </a>
                          <span>{mintyAmount(item.balance)} MINTY</span>
                        </div>
                      ))}
                    </div>
                    {history.length > 0 && (
                      <div className="wallet-history">
                        <strong>Classification history</strong>
                        {history.map((item, j) => (
                          <div key={j}>
                            <time>
                              {item.changedAt
                                ? date(String(item.changedAt))
                                : "UNKNOWN"}
                            </time>
                            <span>
                              {String(item.reason ?? "Classification updated")}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </details>
              </article>
            );
          })}
        </div>
      </details>
      {notice && <div className="toast">{notice}</div>}
    </div>
  );
}

export function Visibility({ data, refresh }: ActionProps) {
  const navigate=useNavigate();
  const [busy,setBusy]=useState('');
  const [notice,setNotice]=useState('');
  const v=data.visibility;
  const xAccount=data.bridge.accounts.find(account=>account.platform.toUpperCase()==='X');
  async function regenerate(){setBusy('refresh');setNotice('');try{await post('/visibility/refresh');await refresh();setNotice('Today’s plan was rebuilt from the latest cached intelligence. No paid call was made.')}catch(error){setNotice((error as Error).message)}finally{setBusy('')}}
  async function decide(id:string,status:'IN REVIEW'|'DISMISSED',intelligenceId:string|null,media:string){setBusy(id);setNotice('');try{await post(`/visibility/opportunities/${id}/status`,{status});await refresh();if(status==='IN REVIEW'&&intelligenceId){const type=/video|reel|\+/.test(media.toLowerCase())?'FULL_CAMPAIGN':/carousel/.test(media.toLowerCase())?'CAROUSEL':/meme/.test(media.toLowerCase())?'MEME':'IMAGE';navigate(`/create/media?story=${intelligenceId}&type=${type}`)}}catch(error){setNotice((error as Error).message)}finally{setBusy('')}}
  const best=v.opportunities[0];
  return <div className="visibility-page">
    <PageIntro eyebrow="MINTY GROWTH · VISIBILITY" title="What should Minty do today?" body="A focused, evidence-led plan for earning relevance—not another content dashboard." actions={<button className="button button-subtle" disabled={busy==='refresh'} onClick={regenerate}><RefreshCw/>{busy==='refresh'?'Reviewing…':'Refresh plan'}</button>}/>
    <GrowthTabs/>
    <div className="visibility-safety"><LockKeyhole/><span>LIVE — MANUAL APPROVAL ONLY</span><small>0 additional paid calls · no automatic posts or replies</small></div>
    {v.plan?<Card className="visibility-brief"><div><Eyebrow>TODAY’S PLAN · {new Date(v.plan.plan_date).toLocaleDateString('en-GB',{weekday:'long',day:'numeric',month:'long'})}</Eyebrow><h2>{v.plan.strategy_summary}</h2></div><div className="visibility-channel"><span>X spearhead</span><Status tone={xAccount?.connection_health.state==='HEALTHY'?'good':'warn'}>{xAccount?.connection_health.state??'NOT MAPPED'}</Status><small>{xAccount?.connection_health.detail??'Minty HQ will not pretend this channel is healthy.'}</small></div></Card>:<Card><Empty title="No strong move today" body="Minty will not manufacture activity when the evidence does not support a useful contribution."/></Card>}
    {best&&<Card className="visibility-best"><div className="visibility-rank"><small>BEST OPPORTUNITY</small><strong>{best.visibility_score}</strong><span>Visibility score</span></div><div><Eyebrow>{best.move_type} MOVE · {best.platform} · {best.content_pillar}</Eyebrow><h2>{best.title}</h2><p>{best.proposed_contribution}</p><blockquote>{best.proposed_hook}</blockquote><div className="visibility-meta"><span><Sparkles/> {best.recommended_media}</span><span><Search/> {best.primary_entity??'Entity pending'}</span><span><ExternalLink/> {humanize(best.link_decision)}</span></div></div><div className="visibility-actions"><button className="button button-primary" disabled={busy===best.id} onClick={()=>decide(best.id,'IN REVIEW',best.intelligence_item_id,best.recommended_media)}>Review <ArrowRight/></button><button className="text-button" disabled={busy===best.id} onClick={()=>decide(best.id,'DISMISSED',null,best.recommended_media)}>Not today</button></div></Card>}
    <section className="visibility-plan"><div className="section-line"><div><Eyebrow>RECOMMENDED MOVES</Eyebrow><h2>Quality over volume.</h2></div><span>{v.opportunities.length} useful action{v.opportunities.length===1?'':'s'}</span></div>{v.opportunities.slice(1).map(item=><Card key={item.id} className="visibility-move"><div className="move-number">0{item.plan_position}</div><div><div className="move-kicker"><Status>{item.action_type}</Status><span>{item.platform} · {item.content_pillar}</span></div><h3>{item.title}</h3><p>{item.why_now}</p><small>{item.proposed_contribution}</small></div><div className="move-media"><Eyebrow>MEDIA</Eyebrow><strong>{item.recommended_media}</strong><span>$MINTY: {item.minty_role}</span></div><div className="visibility-actions"><button className="button button-subtle" disabled={busy===item.id} onClick={()=>decide(item.id,'IN REVIEW',item.intelligence_item_id,item.recommended_media)}>Review</button><button className="text-button" disabled={busy===item.id} onClick={()=>decide(item.id,'DISMISSED',null,item.recommended_media)}>Dismiss</button></div></Card>)}</section>
    <div className="growth-two-col">
      <Card><Eyebrow><MessageCircle/> CONVERSATIONS</Eyebrow><h2>{v.conversations.length?'Join only when Minty adds value.':'No reply worth forcing.'}</h2>{v.conversations.length?v.conversations.map(item=><div className="visibility-conversation" key={item.id}><strong>{item.title}</strong><p>{item.proposed_contribution}</p><Status>{item.status}</Status></div>):<p>Generic replies, token shilling and automated engagement stay blocked.</p>}</Card>
      <Card><Eyebrow>CONTENT QUEUE</Eyebrow><h2>{v.contentQueue.length?`${v.contentQueue.length} item${v.contentQueue.length===1?'':'s'} already in motion.`:'Nothing waiting.'}</h2>{v.contentQueue.slice(0,5).map(item=><div className="queue-line" key={String(item.id)}><span>{String(item.platform)} · {String(item.format)}</span><strong>{String(item.title)}</strong><Status>{String(item.status)}</Status></div>)}{!v.contentQueue.length&&<p>Approved work will still move through claim check, human approval and an explicit Now/Later decision.</p>}</Card>
    </div>
    <Card className="visibility-learning"><div><Eyebrow>WHAT’S WORKING?</Eyebrow><h2>{v.weekly.summary}</h2><p>{v.weekly.learningReady?'Use measured results to change one variable at a time.':'Minty needs at least five real measured posts before recommending winners.'}</p></div><div><strong>{v.weekly.measuredPosts}</strong><span>measured posts</span></div><div><strong>{v.weekly.impressions}</strong><span>impressions</span></div><div><strong>{v.weekly.engagement}</strong><span>engagements</span></div></Card>
    <details className="growth-advanced"><summary>Entities, narrative ownership and quality details <ChevronDown/></summary><div className="growth-two-col"><Card><Eyebrow>ENTITY INTELLIGENCE</Eyebrow><div className="definition-list">{v.entities.length?v.entities.slice(0,6).map((item,i)=><Definition key={i} label={String(item.name)} value={`${item.coverage_count} plans · score ${item.average_score}`}/>):<p>No repeat entity pattern yet.</p>}</div></Card><Card><Eyebrow>NARRATIVE OWNERSHIP</Eyebrow><h2>{v.weekly.learningReady?'Evidence is accumulating.':'Authority is not claimed yet.'}</h2><div className="definition-list">{v.narratives.slice(0,6).map((item,i)=><Definition key={i} label={String(item.name)} value={`${item.coverage_count} coverage · ${item.authority_score} opportunity`}/>)}</div></Card></div></details>
    <p className="manual-only"><LockKeyhole/> Nothing on this page can publish. Review opens the existing Creative Director and Media Engine workflow.</p>
    {notice&&<div className="toast">{notice}</div>}
  </div>;
}

export function Cases({ data, refresh }: ActionProps) {
  const navigate = useNavigate();
  const [selected, setSelected] = useState<CaseItem | null>(
    data.cases[0] ?? null,
  );
  const [notice, setNotice] = useState("");
  useEffect(() => {
    if (selected)
      setSelected(data.cases.find((x) => x.id === selected.id) ?? null);
  }, [data]);
  async function change(status: string) {
    if (!selected) return;
    try {
      await post(`/cases/${selected.id}/transition`, { status });
      await refresh();
      setNotice(`Case moved to ${humanize(status)}.`);
    } catch (e) {
      setNotice((e as Error).message);
    }
  }
  return (
    <>
      <PageIntro
        eyebrow="CASES ARCHIVE"
        title="Every case, when you need it."
        body="Cases organise the work underneath. You don’t need to manage them to create every day."
      />
      <div className="archive-layout">
        <div className="archive-list">
          {data.cases.map((item) => (
            <button
              key={item.id}
              className={selected?.id === item.id ? "active" : ""}
              onClick={() => setSelected(item)}
            >
              <span>CASE #{String(item.case_number).padStart(4, "0")}</span>
              <h3>{item.title}</h3>
              <footer>
                <Status>{humanize(item.status)}</Status>
                <b>{item.viral_score}</b>
              </footer>
            </button>
          ))}
          {!data.cases.length && (
            <Empty
              title="No cases yet"
              body="A case is created automatically when you turn a story into content."
            />
          )}
        </div>
        {selected && (
          <Card className="case-detail">
            <div className="editor-title">
              <div>
                <Eyebrow>
                  CASE #{String(selected.case_number).padStart(4, "0")}
                </Eyebrow>
                <h2>{selected.title}</h2>
              </div>
              <Score value={selected.viral_score} size="lg" />
            </div>
            <p>
              {selected.minty_angle?.detectiveAngle ??
                "This case is waiting for a clear Minty angle."}
            </p>
            <div className="definition-list">
              <Definition label="Narrative" value={selected.narrative} />
              <Definition
                label="Priority"
                value={humanize(selected.priority)}
              />
              <Definition label="Status" value={humanize(selected.status)} />
              <Definition label="Created" value={date(selected.created_at)} />
            </div>
            <div className="case-actions">
              <label>
                Status
                <select
                  value={selected.status}
                  onChange={(e) => change(e.target.value)}
                >
                  {[
                    "OPEN",
                    "RESEARCHING",
                    "READY",
                    "APPROVED",
                    "SCHEDULED",
                    "LIVE",
                    "CLOSED",
                    "ARCHIVED",
                  ].map((x) => (
                    <option key={x}>{x}</option>
                  ))}
                </select>
              </label>
              <button
                className="button button-primary"
                onClick={() => navigate("/create")}
              >
                Create content
              </button>
            </div>
            {notice && <div className="inline-notice">{notice}</div>}
          </Card>
        )}
      </div>
    </>
  );
}

export function Market({ data }: PageProps) {
  const records = data.evidence.filter((x) =>
    x.evidence_key.startsWith("blockchain."),
  );
  return (
    <>
      <PageIntro
        eyebrow="MARKET"
        title="Minty, as it stands."
        body="The latest verified market picture, with uncertainty shown honestly."
      />
      <Card className="market-summary">
        <div>
          <Eyebrow>MINTY</Eyebrow>
          <h2>{extractEvidence(records, "price") || "Price unavailable"}</h2>
          <Status tone="amber">{data.market.status}</Status>
        </div>
        <div className="snapshot-grid">
          <Snapshot
            value={extractLiquidity(data.market.liquidity)}
            label="Liquidity"
          />
          <Snapshot
            value={extractEvidence(records, "volume") || "Unknown"}
            label="Volume"
          />
          <Snapshot
            value={extractEvidence(records, "token_accounts") || "Unknown"}
            label="Token accounts"
          />
          <Snapshot
            value={extractEvidence(records, "supply") || "Unknown"}
            label="Supply"
          />
        </div>
        <p>
          Last updated{" "}
          {data.market.observedAt ? date(data.market.observedAt) : "not yet"}
        </p>
        <details className="progressive-details">
          <summary>
            View details <ChevronDown />
          </summary>
          <div className="definition-list">
            {records.map((r) => (
              <Definition
                key={r.id}
                label={r.label}
                value={renderValue(r.value)}
              />
            ))}
          </div>
          <div className="mint-address">
            <code>{data.market.mint}</code>
            <button
              onClick={() => navigator.clipboard.writeText(data.market.mint)}
            >
              <Copy />
              Copy
            </button>
          </div>
        </details>
      </Card>
    </>
  );
}

export function Evidence({ data }: PageProps) {
  const [filter, setFilter] = useState("ALL");
  const states = [
    "ALL",
    ...new Set(data.evidence.map((x) => x.verification_state)),
  ];
  const records =
    filter === "ALL"
      ? data.evidence
      : data.evidence.filter((x) => x.verification_state === filter);
  return (
    <>
      <PageIntro
        eyebrow="THE EVIDENCE ROOM"
        title="Don’t trust Detective Minty. Verify him."
        body="Every important claim, source and review state—kept in one calm, inspectable record."
      />
      <div className="filter-row">
        {states.map((s) => (
          <button
            key={s}
            className={filter === s ? "active" : ""}
            onClick={() => setFilter(s)}
          >
            {humanize(s)}
          </button>
        ))}
      </div>
      <Card className="evidence-list">
        {records.map((r) => (
          <details key={r.id}>
            <summary>
              <div>
                <strong>{r.label}</strong>
                <small>
                  {r.observed_at
                    ? `Observed ${date(r.observed_at)}`
                    : "No observation date"}
                </small>
              </div>
              <span>{renderValue(r.value)}</span>
              <Status>{humanize(r.verification_state)}</Status>
              <ChevronDown />
            </summary>
            <div>
              <Definition label="Source" value={renderValue(r.source)} />
              <Definition
                label="Reviewed by"
                value={r.reviewer ?? "Not reviewed"}
              />
              <p>{r.notes ?? "No additional notes."}</p>
            </div>
          </details>
        ))}
      </Card>
    </>
  );
}

export function Distro({ data, refresh }: ActionProps) {
  const [notice, setNotice] = useState("");
  async function action(id: string, type: "status" | "cancel" | "performance") {
    try {
      await post(`/publish-jobs/${id}/${type}`);
      await refresh();
      setNotice(
        type === "status"
          ? "Status refreshed."
          : type === "cancel"
            ? "Scheduled post cancelled."
            : "Results refreshed.",
      );
    } catch (e) {
      setNotice((e as Error).message);
    }
  }
  return (
    <>
      <PageIntro
        eyebrow="CONNECTED ACCOUNTS"
        title="Minty’s publishing channels."
        body="When everything is healthy, publishing simply works. Technical details stay out of the way."
        actions={
          <Status tone={data.bridge.state === "CONNECTED" ? "good" : "warn"}>
            {friendlyBridge(data.bridge.state)}
          </Status>
        }
      />
      <div className="account-cards">
        {data.bridge.accounts.map((a) => (
          <Card key={a.id}>
            <div className="account-symbol">{a.platform.slice(0, 1)}</div>
            <div>
              <h2>{a.display_name ?? a.handle}</h2>
              <p>
                {a.platform} · @{a.handle}
              </p>
            </div>
            <Status
              tone={accountHealthy(a.connection_health.state) ? "good" : "warn"}
            >
              {accountHealthy(a.connection_health.state)
                ? "Connected"
                : "Needs attention"}
            </Status>
          </Card>
        ))}
      </div>
      <Card className="bridge-simple">
        <ShieldCheck />
        <div>
          <h2>Manual approval is on.</h2>
          <p>
            Minty HQ decides what, where and when. Publishing happens only after
            your explicit approval.
          </p>
        </div>
      </Card>
      <details className="technical-panel">
        <summary>
          Technical details <ChevronDown />
        </summary>
        <Card>
          <div className="definition-list">
            <Definition label="Connection" value={data.bridge.state} />
            <Definition
              label="Mode"
              value={data.bridge.mode ?? data.bridge.state}
            />
            <Definition
              label="Account mapping"
              value={data.bridge.accountMappingState}
            />
            <Definition
              label="Last successful check"
              value={
                data.bridge.lastSuccessfulCheck
                  ? date(data.bridge.lastSuccessfulCheck)
                  : "Never"
              }
            />
            <Definition
              label="Last error"
              value={data.bridge.lastError ?? "None"}
            />
          </div>
          <h3>Recent publishing activity</h3>
          <div className="job-list">
            {data.publishJobs.map((job) => (
              <div key={job.id}>
                <div>
                  <strong>{job.content.title}</strong>
                  <small>
                    {job.platform} · {simpleStatus(job.status)} ·{" "}
                    {date(job.created_at)}
                  </small>
                </div>
                <Status tone={statusTone(job.status)}>
                  {simpleStatus(job.status)}
                </Status>
                <button onClick={() => action(job.id, "status")}>
                  Refresh
                </button>
                {["QUEUED", "SCHEDULED", "RECEIVED"].includes(job.status) && (
                  <button onClick={() => action(job.id, "cancel")}>
                    Cancel
                  </button>
                )}
                <button onClick={() => action(job.id, "performance")}>
                  Results
                </button>
              </div>
            ))}
          </div>
          {notice && <div className="inline-notice">{notice}</div>}
        </Card>
      </details>
    </>
  );
}

export function SettingsPage({ data, refresh }: ActionProps) {
  const [notice, setNotice] = useState("");
  const [test, setTest] = useState<Record<string, any> | null>(null);
  const [testing, setTesting] = useState(false);
  const candidate = data.intelligence.find(
    (x) => !x.is_mock && x.risk_rating !== "HIGH",
  );
  const ai = data.ai,
    x = data.xSearch;
  return (
    <>
      <PageIntro
        eyebrow="SETTINGS"
        title="The machinery, quietly organised."
        body="Advanced controls live here so the everyday experience can stay simple."
      />
      <div className="settings-list">
        <details open>
          <summary>
            <span>
              <Sparkles />
              Minty Intelligence V2
            </span>
            <ChevronDown />
          </summary>
          <div>
            <div className="ai-health">
              <Status
                tone={
                  ai.state === "CONNECTED"
                    ? "good"
                    : ai.state === "ERROR"
                      ? "bad"
                      : "warn"
                }
              >
                {humanize(ai.state)}
              </Status>
              <span>
                {ai.provider === "xai"
                  ? "Grok intelligence"
                  : "Rule-based fallback"}{" "}
                · {ai.model}
              </span>
            </div>
            <div className="usage-grid">
              <Metric
                value={`${ai.today.requests}/${ai.limits.dailyRequests}`}
                label="Standard requests today"
              />
              <Metric
                value={`$${ai.today.cost.toFixed(3)}`}
                label="Standard cost today"
              />
              <Metric
                value={`${x.today.operations}/${x.dailyLimit}`}
                label="X Search operations"
              />
              <Metric
                value={`$${(x.today.modelCost + x.today.toolCost).toFixed(3)}`}
                label="X Search cost today"
              />
            </div>
            <Definition
              label="X Search budget"
              value={`${x.today.remaining} operations remaining · $${x.unitToolCost.toFixed(3)} current tool rate`}
            />
            <Definition
              label="Total Minty AI today"
              value={`$${(ai.today.cost + x.today.modelCost + x.today.toolCost).toFixed(3)}`}
            />
            <Definition label="Prompt" value={ai.promptVersion} />
            <Definition
              label="Guardrails"
              value={`${ai.limits.maxOutputTokens} max tokens · ${ai.limits.concurrency} at a time · manual approval only`}
            />
            {x.lastRun && (
              <Definition
                label="Last X mission"
                value={`${humanize(x.lastRun.mission)} · ${humanize(x.lastRun.status)}`}
              />
            )}
            <Definition
              label="Tokens today"
              value={`${ai.today.inputTokens.toLocaleString()} in · ${ai.today.outputTokens.toLocaleString()} out`}
            />
            {ai.byTask.length > 0 && (
              <div className="usage-tasks">
                {ai.byTask.map((row) => (
                  <div key={row.task}>
                    <span>{humanize(row.task)}</span>
                    <strong>
                      {row.requests} · ${row.cost.toFixed(3)}
                    </strong>
                  </div>
                ))}
              </div>
            )}
            {ai.lastError && (
              <p className="budget-note">
                <AlertCircle /> AI is using safe fallback: {ai.lastError}
              </p>
            )}
            <p className="safety-note">
              <LockKeyhole /> Credentials stay in an isolated server secret. No
              AI action can publish.
            </p>
            {candidate && (
              <button
                className="button button-subtle"
                disabled={testing}
                onClick={async () => {
                  setTesting(true);
                  setNotice("");
                  try {
                    const result = await post<Record<string, any>>(
                      `/ai/safe-test/${candidate.id}`,
                    );
                    setTest(result);
                    await refresh();
                    setNotice(
                      "Safe one-story pipeline test complete. Nothing was published.",
                    );
                  } catch (e) {
                    setNotice((e as Error).message);
                  } finally {
                    setTesting(false);
                  }
                }}
              >
                <Sparkles />
                {testing ? "Running safe test…" : "Run one-story safe test"}
              </button>
            )}
            {test && (
              <details className="test-result" open>
                <summary>Safe pipeline result</summary>
                <div>
                  <Definition
                    label="Filter"
                    value={String(test.filter?.reason ?? "Complete")}
                  />
                  <Definition
                    label="Heuristic score"
                    value={String(test.heuristicScore)}
                  />
                  <Definition label="Grok score" value={String(test.aiScore)} />
                  <Definition
                    label="Final score"
                    value={String(test.finalScore)}
                  />
                  <p>
                    <strong>Angle:</strong>{" "}
                    {String(test.angle?.detectiveAngle ?? "")}
                  </p>
                  <p>
                    <strong>Content preview:</strong>{" "}
                    {String(test.contentPreview?.body ?? "")}
                  </p>
                </div>
              </details>
            )}
          </div>
        </details>
        <details>
          <summary>
            <span>
              <FileText />
              Media providers & usage
            </span>
            <ChevronDown />
          </summary>
          <div>
            <div className="usage-grid">
              <Metric
                value={`${data.media.usage.today.find((row) => row.category === "IMAGE")?.requests ?? 0}/${data.media.usage.caps.imageDailyGenerations}`}
                label="Image renders today"
              />
              <Metric
                value={`$${Number(data.media.usage.today.find((row) => row.category === "IMAGE")?.cost ?? 0).toFixed(3)}`}
                label="Image cost today"
              />
              <Metric value="0" label="Video renders today" />
              <Metric
                value={`$${data.media.usage.caps.videoDailyUsd.toFixed(2)}`}
                label="Video daily cap"
              />
            </div>
            {data.media.providers.map((provider) => (
              <Definition
                key={provider.id}
                label={`${provider.kind} · ${provider.provider}`}
                value={`${humanize(provider.health)} · ${provider.model}`}
              />
            ))}
            <Definition
              label="Storage"
              value={`${(data.media.storage.freeBytes / 1024 ** 3).toFixed(1)} GB VPS free · ${(data.media.storage.usedBytes / 1024 ** 2).toFixed(1)} MB Minty media used`}
            />
            <p className="safety-note">
              <LockKeyhole /> Paid media providers are never enabled or charged
              without explicit configuration. Video rendering stops below the
              configured disk floor.
            </p>
          </div>
        </details>
        <details>
          <summary>
            <span>
              <Telescope />
              Intelligence sources
            </span>
            <ChevronDown />
          </summary>
          <div className="settings-sources">
            {data.sources.map((s) => (
              <div key={s.id}>
                <div>
                  <strong>{s.name}</strong>
                  <small>
                    {s.source_role} · {s.signal_type} · every{" "}
                    {s.polling_interval_minutes}m · quality {s.source_quality}
                    {s.source_health ? ` · ${humanize(s.source_health)}` : ""}
                    {s.last_error ? ` · ${s.last_error}` : ""}
                  </small>
                </div>
                <Status>
                  {s.is_mock ? "Demo" : s.enabled ? "Active" : "Off"}
                </Status>
                {["rss", "reddit", "trends", "dex"].includes(s.adapter) && (
                  <button
                    className="button button-subtle"
                    onClick={async () => {
                      try {
                        const r = await post<{
                          created: number;
                          clustered: number;
                        }>(`/sources/${s.id}/ingest`);
                        await refresh();
                        setNotice(
                          `${r.created} new signals · ${r.clustered} grouped.`,
                        );
                      } catch (e) {
                        setNotice((e as Error).message);
                      }
                    }}
                  >
                    <RefreshCw />
                    Refresh
                  </button>
                )}
              </div>
            ))}
          </div>
        </details>
        <details>
          <summary>
            <span>
              <BarChart3 />
              Scoring priorities
            </span>
            <ChevronDown />
          </summary>
          <ScoreBars scores={data.settings.score_weights ?? {}} suffix="%" />
          <div className="check-list">
            <div><CheckCircle2/>Creative review {data.settings.creative_thresholds?.creativeReview ?? 68}+</div>
            <div><CheckCircle2/>Hot {data.settings.creative_thresholds?.hot ?? 75}+</div>
            <div><CheckCircle2/>Breaking {data.settings.creative_thresholds?.breaking ?? 85}+</div>
            <div><CheckCircle2/>Minty fit {data.settings.creative_thresholds?.mintyFit ?? 65}+</div>
          </div>
        </details>
        <details>
          <summary>
            <span>
              <ShieldCheck />
              Security & system
            </span>
            <ChevronDown />
          </summary>
          <div className="check-list">
            {[
              "68-point Creative Director threshold",
              "Independent Minty-fit gate",
              "Separate X Search accounting",
              "Server-side secrets",
              "Manual approval only",
              "No autonomous publishing",
            ].map((x) => (
              <div key={x}>
                <CheckCircle2 />
                {x}
              </div>
            ))}
          </div>
        </details>
      </div>
      {notice && <div className="toast">{notice}</div>}
    </>
  );
}

function AddStory({ onSaved }: { onSaved: () => Promise<void> }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const fd = new FormData(e.currentTarget);
      await post("/intelligence", Object.fromEntries(fd));
      await onSaved();
      e.currentTarget.reset();
      document.getElementById("new-story")?.hidePopover();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <form
      id="new-story"
      popover="auto"
      className="popover-form"
      onSubmit={submit}
    >
      <Eyebrow>ADD STORY</Eyebrow>
      <h2>What should Minty watch?</h2>
      <label>
        Headline
        <input name="title" required />
      </label>
      <label>
        Short summary
        <textarea name="summary" required />
      </label>
      <div className="form-grid">
        <label>
          Source
          <input name="sourceName" required />
        </label>
        <label>
          Source link
          <input name="sourceUrl" type="url" required />
        </label>
        <label>
          Category
          <input name="category" required />
        </label>
        <label>
          Theme
          <input name="narrative" required />
        </label>
      </div>
      {error && <div className="form-error">{error}</div>}
      <div className="form-actions">
        <button
          type="button"
          className="button button-subtle"
          popoverTarget="new-story"
          popoverTargetAction="hide"
        >
          Cancel
        </button>
        <button className="button button-primary" disabled={busy}>
          {busy ? "Adding…" : "Add story"}
        </button>
      </div>
    </form>
  );
}
function SectionHeading({
  title,
  action,
  onClick,
}: {
  title: string;
  action?: string;
  onClick?: () => void;
}) {
  return (
    <div className="section-heading">
      <h2>{title}</h2>
      {action && (
        <button onClick={onClick}>
          {action}
          <ChevronRight />
        </button>
      )}
    </div>
  );
}
function AttentionRow({
  count,
  label,
  tone,
  onClick,
}: {
  count: number;
  label: string;
  tone: string;
  onClick: () => void;
}) {
  return (
    <button onClick={onClick}>
      <span className={`attention-count ${tone}`}>{count}</span>
      <strong>{label}</strong>
      <ChevronRight />
    </button>
  );
}
function Snapshot({ value, label }: { value: string; label: string }) {
  return (
    <div>
      <strong>{value}</strong>
      <span>{label}</span>
    </div>
  );
}
function Metric({ value, label }: { value: string; label: string }) {
  return (
    <Card>
      <strong>{value}</strong>
      <span>{label}</span>
    </Card>
  );
}
function Definition({ label, value }: { label: string; value: string }) {
  return (
    <div className="definition">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}
function Trust({ ok, label }: { ok: boolean; label: string }) {
  return (
    <div className={ok ? "ok" : "bad"}>
      {ok ? <Check /> : <AlertCircle />}
      <strong>{label}</strong>
    </div>
  );
}
function Step({
  n,
  label,
  active,
  done,
}: {
  n: string;
  label: string;
  active: boolean;
  done: boolean;
}) {
  return (
    <div className={`${active ? "active" : ""} ${done ? "done" : ""}`}>
      <span>{done ? <Check /> : n}</span>
      <strong>{label}</strong>
    </div>
  );
}
function ScoreBars({
  scores,
  suffix = "",
}: {
  scores: Record<string, number>;
  suffix?: string;
}) {
  return (
    <div className="score-bars">
      {Object.entries(scores).map(([key, value]) => (
        <div key={key}>
          <span>{humanize(key)}</span>
          <i>
            <b style={{ width: `${Math.min(100, value)}%` }} />
          </i>
          <strong>
            {value}
            {suffix}
          </strong>
        </div>
      ))}
    </div>
  );
}
type CreativeThresholds = { creativeReview:number;hot:number;breaking:number;mintyFit:number };
function creativeThresholds(data:Bootstrap):CreativeThresholds{return{creativeReview:68,hot:75,breaking:85,mintyFit:65,...(data.settings.creative_thresholds??{})}}
function storyStatus(score: number, thresholds:CreativeThresholds={creativeReview:68,hot:75,breaking:85,mintyFit:65}) {
  return score >= thresholds.breaking
    ? "BREAKING CASE"
    : score >= thresholds.hot
      ? "HOT CASE"
      : score >= thresholds.creativeReview
        ? "GOOD CASE"
        : score >= 60
          ? "WATCH"
          : "LOW PRIORITY";
}
function toneForScore(score: number, thresholds:CreativeThresholds={creativeReview:68,hot:75,breaking:85,mintyFit:65}) {
  return score >= thresholds.hot ? "bad" : score >= thresholds.creativeReview ? "warn" : "neutral";
}
function momentumLabel(item: IntelligenceItem) {
  const arrows =
    item.momentum_state === "EXPLODING"
      ? "↑↑↑"
      : item.momentum_state === "RISING FAST"
        ? "↑↑"
        : item.momentum_state === "RISING"
          ? "↑"
          : item.momentum_state === "FADING"
            ? "↓"
            : "→";
  return `${item.momentum_state || "UNKNOWN"} ${arrows}`;
}
function statusTone(status: string) {
  return /PUBLISHED|APPROVED|CONNECTED/.test(status)
    ? "good"
    : /FAILED|ERROR|BLOCKED/.test(status)
      ? "bad"
      : /REVIEW|SCHEDULED|QUEUED/.test(status)
        ? "warn"
        : "neutral";
}
function simpleStatus(status: string) {
  if (status === "AI CHECKED" || status === "HUMAN REVIEW") return "Ready";
  if (status === "ERROR" || status === "FAILED" || status === "BLOCKED")
    return "Problem";
  return humanize(status);
}
function platformIcon(platform: string) {
  return platform === "X"
    ? "𝕏"
    : platform === "Instagram"
      ? "◎"
      : platform.includes("Reel")
        ? "▶"
        : platform === "Telegram"
          ? "✈"
          : platform === "Campaign"
            ? "✦"
            : "⌁";
}
function platformFor(platform: string) {
  return platform === "Reel / TikTok"
    ? "TikTok"
    : platform === "Campaign" || platform === "Case File"
      ? "MintyCoin.org"
      : platform;
}
function formatFor(platform: string) {
  return platform === "X"
    ? "X short post"
    : platform === "Instagram"
      ? "Instagram caption"
      : platform === "Reel / TikTok"
        ? "Reel/TikTok short script"
        : platform === "Telegram"
          ? "Telegram/community post"
          : platform === "Campaign"
            ? "Campaign concept"
            : "MintyCoin.org Case File outline";
}
function greeting() {
  const h = new Date().getHours();
  return h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
}
function relative(v: string) {
  const ms = Date.now() - new Date(v).getTime();
  const m = Math.round(ms / 60000);
  const h = Math.round(ms / 3600000);
  return m < 1
    ? "Just now"
    : m < 60
      ? `${m}m ago`
      : h < 24
        ? `${h}h ago`
        : `${Math.round(h / 24)}d ago`;
}
function date(v: string) {
  return new Intl.DateTimeFormat("en", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(v));
}
function humanize(v: string) {
  const spaced =
    v === v.toUpperCase()
      ? v.replaceAll("_", " ")
      : v.replace(/([A-Z])/g, " $1").replaceAll("_", " ");
  return spaced.toLowerCase().replace(/^./, (x) => x.toUpperCase());
}
function mintyAmount(value: unknown) {
  const amount = Number(value ?? 0);
  return Number.isFinite(amount)
    ? amount.toLocaleString(undefined, { maximumFractionDigits: 9 })
    : "UNKNOWN";
}
function shortAddress(value: string) {
  return value.length > 16 ? `${value.slice(0, 8)}…${value.slice(-6)}` : value;
}
function extractCount(value: unknown) {
  if (value && typeof value === "object" && "count" in value)
    return String((value as { count: unknown }).count);
  return "Unknown";
}
function extractLiquidity(value: unknown) {
  if (
    value &&
    typeof value === "object" &&
    "tvl_usd_reported_by_raydium" in value
  )
    return `$${Number((value as { tvl_usd_reported_by_raydium: unknown }).tvl_usd_reported_by_raydium).toLocaleString()}`;
  return "Unknown";
}
function renderValue(value: unknown) {
  if (value === null || value === undefined) return "Unknown";
  if (typeof value === "string" || typeof value === "number")
    return String(value);
  if (Array.isArray(value)) return `${value.length} records`;
  if (typeof value === "object") {
    const o = value as Record<string, unknown>;
    return String(
      o.display ??
        o.ui_amount ??
        o.count ??
        o.status ??
        o.name ??
        "View source record",
    );
  }
  return String(value);
}
function extractEvidence(records: Bootstrap["evidence"], needle: string) {
  const found = records.find((r) => r.evidence_key.includes(needle));
  return found ? renderValue(found.value) : "";
}
function healthyAccountSummary(data: Bootstrap) {
  const connected = data.bridge.accounts.filter((a) =>
    accountHealthy(a.connection_health.state),
  ).length;
  return connected ? `${connected} connected` : "Needs attention";
}
function recentSentence(status: string, platform: string) {
  if (status === "PUBLISHED")
    return `Last post published successfully on ${platform}.`;
  if (status === "SCHEDULED") return `A ${platform} post is scheduled.`;
  if (status === "CANCELLED")
    return `A scheduled ${platform} post was cancelled.`;
  if (["FAILED", "ERROR"].includes(status))
    return `A ${platform} post needs attention.`;
  return `A ${platform} post is moving through publishing.`;
}
function sameDay(a: Date, b: Date) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}
function startMonth(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function addMonths(d: Date, n: number) {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}
function calendarDays(month: Date) {
  const start = new Date(month);
  const offset = (start.getDay() + 6) % 7;
  start.setDate(start.getDate() - offset);
  return Array.from(
    { length: 42 },
    (_, i) =>
      new Date(start.getFullYear(), start.getMonth(), start.getDate() + i),
  );
}
function localDateTime(d: Date) {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function metricTotal(metrics: Bootstrap["metrics"], key: "views") {
  const values = metrics
    .map((m) => m[key])
    .filter((v): v is number => typeof v === "number");
  return values.length
    ? values.reduce((a, b) => a + b, 0).toLocaleString()
    : "Not available";
}
function bestPlatform(metrics: Bootstrap["metrics"]) {
  const totals = new Map<string, number>();
  metrics.forEach((m) =>
    totals.set(
      m.platform,
      (totals.get(m.platform) ?? 0) +
        (m.likes ?? 0) +
        (m.comments ?? 0) +
        (m.shares ?? 0),
    ),
  );
  return [...totals].sort((a, b) => b[1] - a[1])[0]?.[0] ?? "No platform";
}
function friendlyBridge(state: string) {
  return state === "CONNECTED"
    ? "Connected"
    : state === "DEGRADED"
      ? "Needs attention"
      : state === "ERROR"
        ? "Action required"
        : "Not connected";
}
function accountHealthy(state: string) {
  return state === "HEALTHY" || state === "CONNECTED";
}
function everydayIntelligence(data: Bootstrap) {
  return data.mode === "LIVE SOURCES ONLY"
    ? data.intelligence.filter((item) => !item.is_mock)
    : data.intelligence;
}
